import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import '../styles/TaskProgress.css';
import RequirementTable from '../components/RequirementTable';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import { Select } from 'antd';
import ReactDOM from 'react-dom';

interface ExecutionStatus {
  api_name: string;
  status: string;
  progress: number;
  result?: {
    text?: string;
  };
}

interface Work {
  name: string;
  description: string;
  detailed: string;
}

export type ExportPDFFunction = (type: 'requirements' | 'userStories' | 'techSpecs') => Promise<void>;

interface CrewAIOutputs {
  name: string;
  type: string;
  configs: {
    agents: string[];
    tasks: string[];
  };
  works: Work[];
}

interface Node {
  text: string;
  outputs: CrewAIOutputs;
  status: string;
  progress: number;
}

interface TaskStatus {
  task_id: string;
  name: string;
  description: string;
  status: string;
  created_at: string;
  updated_at: string;
  execution_statuses: ExecutionStatus[];
  nodes?: {
    crewAi?: Node;
    [key: string]: Node | undefined;
  };
  parsed_workflow?: Array<{
    work_name: string;
    work_description: string;
    detailed?: string;  // 使用与Work接口一致的字段名
    work_detailed?: string;  // 保留兼容性
    agent_role: string;
    agent_goal: string;
    agent_backstory: string;
    task_description: string;
    task_expected_output: string;
    tools?: string;
    agent_mcp_tools?: string;
  }>;
}

interface AgentConfig {
  name: string;
  role: string;
  goal: string;
  backstory: string;
  tools?: string;
  agent_mcp_tools?: string;
}

interface SelectedDoc {
  text: string;
  title: string;
  api_name: string;
}

interface TaskProgressProps {
  appId?: string;
  handleExportPDF?: ExportPDFFunction;
}

interface DetailItem {
  type: 'agent';
  name: string;
  goal?: string;
  backstory?: string;
  task_description?: string;
  task_expected_output?: string;
  work_name?: string;
  isEditing?: boolean;
  crewTools?: string[];
  mcpTools?: string[];
}

const parseAgentString = (agentStr: string): AgentConfig => {
  const lines = agentStr.split('\n').filter(line => line.trim());
  // 提取名称（去掉末尾的冒号）
  const name = lines[0].trim().replace(/:$/, '');
  
  // 提取角色，处理可能包含 ">" 符号的情况
  const roleLines = [];
  let roleStarted = false;
  let goalStarted = false;
  let backstoryStarted = false;
  
  // 遍历所有行，收集角色、目标和背景的文本
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    
    if (line.startsWith('role:')) {
      roleStarted = true;
      goalStarted = false;
      backstoryStarted = false;
      // 如果角色在同一行，直接提取
      const restOfLine = line.substring(5).trim();
      if (restOfLine && restOfLine !== '>') {
        roleLines.push(restOfLine);
      }
    } else if (line.startsWith('goal:')) {
      roleStarted = false;
      goalStarted = true;
      backstoryStarted = false;
    } else if (line.startsWith('backstory:')) {
      roleStarted = false;
      goalStarted = false;
      backstoryStarted = true;
    } else if (roleStarted && !line.startsWith('goal:') && !line.startsWith('backstory:')) {
      roleLines.push(line.replace(/^>/, '').trim());
    }
  }
  
  // 提取目标，处理多行情况
  const goalLines = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    
    if (line.startsWith('goal:')) {
      goalStarted = true;
      const restOfLine = line.substring(5).trim();
      if (restOfLine && restOfLine !== '>') {
        goalLines.push(restOfLine);
      }
    } else if (goalStarted && !line.startsWith('role:') && !line.startsWith('backstory:')) {
      goalLines.push(line.replace(/^>/, '').trim());
    }
  }
  
  // 提取背景，处理多行情况
  const backstoryLines = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    
    if (line.startsWith('backstory:')) {
      const restOfLine = line.substring(10).trim();
      if (restOfLine && restOfLine !== '>') {
        backstoryLines.push(restOfLine);
      }
    } else if (backstoryStarted && !line.startsWith('role:') && !line.startsWith('goal:')) {
      backstoryLines.push(line.replace(/^>/, '').trim());
    }
  }

  return {
    name,
    role: roleLines.join(' '),
    goal: goalLines.join(' '),
    backstory: backstoryLines.join(' ')
  };
};

const takeWhile = <T,>(array: T[], predicate: (value: T) => boolean): T[] => {
  const result: T[] = [];
  for (const item of array) {
    if (predicate(item)) {
      result.push(item);
    } else {
      break;
    }
  }
  return result;
};

interface PreviewResponse {
  filename: string;
  full_path: string;
}

const TaskProgress: React.FC<TaskProgressProps> = ({ appId, handleExportPDF }) => {
  const [taskStatus, setTaskStatus] = useState<TaskStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedDoc, setSelectedDoc] = useState<SelectedDoc | null>(null);
  const [editedText, setEditedText] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [isAllCompleted, setIsAllCompleted] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>('Requirement');
  const [appDetail, setAppDetail] = useState<any>(null);
  const [selectedDetail, setSelectedDetail] = useState<DetailItem | null>(null);
  const [firstIncompleteTask, setFirstIncompleteTask] = useState<string | null>(null);
  const [editedDetail, setEditedDetail] = useState<DetailItem | null>(null);
  const [isUpdatingScheduleType, setIsUpdatingScheduleType] = useState(false);
  // 新增：tools弹窗相关state
  const [toolsModal, setToolsModal] = useState<{
    open: boolean;
    workflowIndex: number | null;
    crewTools: string[];
    mcpTools: string[];
    saving: boolean;
  }>({ open: false, workflowIndex: null, crewTools: [], mcpTools: [], saving: false });
  // 在TaskProgress组件顶部添加：
  const [agentUIState, setAgentUIState] = useState<{[key: number]: {
    showPlus: boolean,
    showCodeModal: boolean,
    codeInput: string,
    submitting: boolean
  }}>({});
  
  // 添加调试信息状态
  const [debugInfo, setDebugInfo] = useState<string>('');
  // 在TaskProgress组件顶部添加：
  const plusBtnRef = useRef<(HTMLButtonElement|null)[]>([]);
  const [modalPos, setModalPos] = useState<{[key:number]: {top:number, left:number}}>({});

  // 1. 在组件顶部增加工具相关状态和本地Crew工具数据
  const crewToolsData = [
    {
      tool_name: "ScrapeWebsiteTool",
      description: "一个旨在提取和读取指定网站内容的工具。它能够通过发出HTTP请求和解析接收到的HTML内容来处理各种类型的网页。该工具对于网页抓取任务、数据收集或从网站提取特定信息特别有用。"
    },
    {
      tool_name: "ScrapeElementFromWebsiteTool",
      description: "一个旨在通过使用CSS选择器从网站提取特定元素的工具。该工具允许CrewAI代理抓取网页中的目标内容，使其对于只需要网页特定部分的数据提取任务非常有用。"
    }
  ];
  const [crewToolsList] = useState<any[]>(crewToolsData);
  const [mcpToolsList, setMcpToolsList] = useState<any[]>([]);
  const [loadingMcpTools, setLoadingMcpTools] = useState(false);

  // 前端页面相关状态
  const [webHtmlCode, setWebHtmlCode] = useState<string>('');
  const [isPreviewMode, setIsPreviewMode] = useState<boolean>(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [isGeneratingPreview, setIsGeneratingPreview] = useState<boolean>(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  // 新增：控制代码/预览切换模式
  const [webHtmlViewMode, setWebHtmlViewMode] = useState<'code' | 'preview'>('code');
  // 新增：控制预览模态框
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState<boolean>(false);

  const checkAllCompleted = (statuses: ExecutionStatus[]) => {
    return statuses.every(status => status.progress === 100);
  };

  const fetchTaskStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get<TaskStatus>(
        `https://ceshi.lightark.cc/api/v1/apps/${appId}/latest_task/status`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // 找到第一个未完成的任务
      const incompleteTask = response.data.execution_statuses.find(
        status => status.progress < 100
      );
      
      setFirstIncompleteTask(incompleteTask?.api_name || null);

      // 如果所有执行状态的进度都是100%，则更新任务状态为已完成
      const updatedData = {
        ...response.data,
        status: checkAllCompleted(response.data.execution_statuses) ? '已完成' : 'pending'
      };
      
      setTaskStatus(updatedData);
      
      if (checkAllCompleted(response.data.execution_statuses)) {
        setIsAllCompleted(true);
      }
    } catch (err) {
      setError('获取任务状态失败');
      console.error('获取任务状态失败:', err);
    }
  }, [appId]);

  const fetchAppDetail = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://ceshi.lightark.cc/api/v1/apps/${appId}/detail`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('获取应用详情失败');
      }
      
      const data: any = await response.json();
      console.log("获取到的应用详情数据:", data);
      console.log("🔄 fetchAppDetail - 获取到的应用详情数据:", data);
      
      // 确保app_configs是数组
      if (data && !Array.isArray(data.app_configs)) {
        console.log("修正app_configs格式，原始数据:", data.app_configs);
        // 如果app_configs不是数组，但它存在，尝试转换
        if (data.app_configs && typeof data.app_configs === 'object') {
          data.app_configs = Object.values(data.app_configs);
        } else {
          data.app_configs = [];
        }
      }
      
      // 检查crewAi配置
      const crewAiConfigData = data.app_configs?.find((config: any) => config.api_name === 'crewAi');
      if (crewAiConfigData) {
        console.log("🔄 fetchAppDetail - 找到crewAi配置:", crewAiConfigData);
        console.log("🔄 fetchAppDetail - result.parsed_workflow:", crewAiConfigData.result?.parsed_workflow);
      }
      
      setAppDetail(data);
      console.log("🔄 fetchAppDetail - appDetail状态已更新");
      
      // 从app_configs中提取web_html的代码
      if (data?.app_configs && Array.isArray(data.app_configs)) {
        const webHtmlConfig = data.app_configs.find((config: any) => config.api_name === 'web_html');
        if (webHtmlConfig && webHtmlConfig.result && webHtmlConfig.result.text) {
          setWebHtmlCode(webHtmlConfig.result.text);
        }
      }
      
      // 检查是否存在crewAi配置
      const crewAiConfig = data.app_configs?.find((config: any) => config.api_name === 'crewAi');
      if (crewAiConfig) {
        console.log("找到crewAi配置:", crewAiConfig);
      } else {
        console.log("未找到crewAi配置");
      }
    } catch (error) {
      console.error('获取应用详情失败:', error);
    }
  }, [appId]);

  useEffect(() => {
    fetchTaskStatus();
    // 立即获取应用详情，不等待任务完成
    if (!appDetail) {
      fetchAppDetail();
    }
    
    if (!isAllCompleted) {
      const interval = setInterval(fetchTaskStatus, 4000);
      return () => clearInterval(interval);
    }
  }, [fetchTaskStatus, isAllCompleted]);

  useEffect(() => {
    // 确保应用详情数据始终是最新的
    if (isAllCompleted && appDetail) {
      fetchAppDetail();
    }
  }, [isAllCompleted, fetchAppDetail]);

  // 引入useTranslation
  const { t } = useTranslation();
  
  const getStatusName = (apiName: string): string => {
    if (apiName === 'crewAi') return t('apps.detail.tasks.agentDetails', '智能体');
    if (apiName === 'Requirement') return t('apps.detail.tasks.requirements', '需求分析');
    if (apiName === 'User Stories') return t('apps.detail.tasks.userStories', '用户故事');
    if (apiName === 'Specification') return t('apps.detail.tasks.specification', '技术规范');
    if (apiName === 'web_html') return t('apps.detail.tasks.webHtml', '前端页面');
    return apiName;
  };

  const shouldShowDocIcon = (status: ExecutionStatus): boolean => {
    const docTypes = ['Specification', 'User Stories', 'Requirement', 'web_html'];
    if (!docTypes.includes(status.api_name)) return false;
    
    // 对于 web_html，检查app_configs中是否有数据
    if (status.api_name === 'web_html') {
      // 如果app_configs中有web_html配置，就显示
      if (appDetail?.app_configs) {
        const configs = Array.isArray(appDetail.app_configs) 
          ? appDetail.app_configs 
          : Object.values(appDetail.app_configs);
        const webHtmlConfig = configs.find((config: any) => config.api_name === 'web_html');
        if (webHtmlConfig && (webHtmlConfig.result?.text || webHtmlConfig.config_json?.text)) {
          return true;
        }
      }
      // 如果没有在app_configs中找到，但是状态存在，也显示
      return true;
    }
    
    // 检查节点是否存在且有内容
    const node = taskStatus?.nodes?.[status.api_name];
    return node?.text != null && node?.text !== '';
  };

  // 根据文档类型返回对应的导出类型
  const getExportType = (apiName: string): 'requirements' | 'userStories' | 'techSpecs' | null => {
    switch (apiName) {
      case 'Requirement':
        return 'requirements';
      case 'User Stories':
        return 'userStories';
      case 'Specification':
        return 'techSpecs';
      default:
        return null;
    }
  };

  const handleDocClick = async (status: ExecutionStatus) => {
    console.log('点击文档按钮，完整状态:', status);
    
    try {
      let docText = '';
      let title = getStatusName(status.api_name);
      
      // 检查是否所有任务都已完成
      const isAllTasksCompleted = taskStatus?.execution_statuses.every(
        s => s.progress === 100
      );
      
      if (isAllTasksCompleted) {
        // 如果所有任务都完成，从 detail 接口获取数据
        const response = await fetch(`https://ceshi.lightark.cc/api/v1/apps/${appId}/detail`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.ok) {
          throw new Error('获取应用详情失败');
        }
        
        const data: any = await response.json();
        
        // 从app_configs中获取对应类型的文档内容
        const config = data.app_configs.find((config: any) => config.api_name === status.api_name);
        if (config) {
          docText = config.result.text || '';
        }
      } else {
        // 如果任务未完成，从 taskStatus.nodes 中获取数据
        const node = taskStatus?.nodes?.[status.api_name];
        if (node) {
          docText = node.text || '';
        }
      }
      
      console.log('文档类型:', status.api_name);
      console.log('文档内容:', docText);
      
      if (!docText) {
        console.log('文档内容为空');
        return;
      }
      
      setSelectedDoc({
        text: docText,
        title: title,
        api_name: status.api_name
      });
      setEditedText(docText);
    } catch (error) {
      console.error('获取文档内容失败:', error);
    }
  };

  const handleSaveDoc = async () => {
    if (!selectedDoc || !appId) return;

    try {
      setIsSaving(true);
      const userId = localStorage.getItem('user_id') || 'unknown';
      
      const response = await fetch(`https://ceshi.lightark.cc/api/v1/apps/${appId}/config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          api_name: selectedDoc.api_name,
          config_json: {
            text: editedText
          },
          status: 'active',
          updated_by: userId
        })
      });

      if (!response.ok) {
        throw new Error('保存失败');
      }

      // 更新本地状态
      setSelectedDoc(prev => prev ? { ...prev, text: editedText } : null);
      
      // 如果所有任务已完成，重新获取应用详情
      if (isAllCompleted) {
        await fetchAppDetail();
      }

      alert('保存成功');
      
      // 关闭弹窗
      setSelectedDoc(null);
      
      // 重新获取任务状态
      fetchTaskStatus();
    } catch (error) {
      console.error('保存文档失败:', error);
      alert('保存失败，请重试');
    } finally {
      setIsSaving(false);
    }
  };

  const getDocContent = (apiName: string): string => {
    if (isAllCompleted && appDetail) {
      // 如果所有任务完成，从 appDetail 中获取数据
      const config = appDetail.app_configs.find((config: any) => config.api_name === apiName);
      return config?.result?.text || '';
    } else {
      // 如果任务未完成，从 taskStatus.nodes 中获取数据
      return taskStatus?.nodes?.[apiName]?.text || '等待执行...';
    }
  };

  // 添加获取parsed_workflow的辅助函数
  const getParsedWorkflow = (): any[] => {
    console.log("=== getParsedWorkflow 开始 ===");
    console.log("taskStatus:", taskStatus);
    console.log("appDetail:", appDetail);
    
    // 🔥 修复优先级：优先从taskStatus中获取最新数据（因为inject_agent_code更新了TaskExecutionStatus表）
    if (taskStatus?.parsed_workflow && taskStatus.parsed_workflow.length > 0) {
      console.log("✅ 从taskStatus获取到parsed_workflow数据:", taskStatus.parsed_workflow);
      console.log("agent数量:", taskStatus.parsed_workflow.length);
      return taskStatus.parsed_workflow;
    }
    
    // 如果taskStatus中没有，才尝试从appDetail获取
    if (appDetail?.app_configs) {
      // 确保app_configs是数组
      const configs = Array.isArray(appDetail.app_configs) 
        ? appDetail.app_configs 
        : Object.values(appDetail.app_configs);
      
      console.log("appDetail.app_configs数组:", configs);
      
      const crewAiConfig = configs.find((config: any) => config.api_name === 'crewAi');
      console.log("✅ 在appDetail中找到的crewAiConfig:", crewAiConfig);
      
      if (crewAiConfig?.result?.parsed_workflow && Array.isArray(crewAiConfig.result.parsed_workflow) && crewAiConfig.result.parsed_workflow.length > 0) {
        console.log("✅ 从appDetail的crewAiConfig中获取到parsed_workflow数据:", crewAiConfig.result.parsed_workflow);
        console.log("agent数量:", crewAiConfig.result.parsed_workflow.length);
        return crewAiConfig.result.parsed_workflow;
      }
    }
    
    console.log("❌ 未找到parsed_workflow数据");
    console.log("taskStatus详情:", JSON.stringify(taskStatus, null, 2));
    console.log("appDetail详情:", JSON.stringify(appDetail, null, 2));
    // 如果都没有找到，返回空数组
    return [];
  };

  const handleDetailClick = async (type: 'agent', data: string) => {
    try {
      console.log("handleDetailClick 收到的数据:", data);
      
      // 检查是否是 parsed_workflow 数据（JSON字符串）
      try {
        const parsedData = JSON.parse(data);
        console.log("解析后的JSON数据:", parsedData);
        
        if (parsedData["agent Role"] || parsedData.agent_role) {
          // 先获取最新数据
          if (appId) {
            try {
              // 尝试获取最新的工作流数据
              const token = localStorage.getItem('token');
              const response = await axios.get(
                `https://ceshi.lightark.cc/api/v1/apps/${appId}/detail`,
                {
                  headers: {
                    'Authorization': `Bearer ${token}`
                  }
                }
              );
              
              // 查找crewAi配置
              const appData = response.data as any;
              
              // 确保app_configs存在且是数组或可转为数组的对象
              if (appData && appData.app_configs) {
                let configs: any[] = [];
                
                if (Array.isArray(appData.app_configs)) {
                  configs = appData.app_configs;
                } else if (typeof appData.app_configs === 'object') {
                  configs = Object.values(appData.app_configs);
                }
                
                const crewAiConfig = configs.find((config: any) => config && config.api_name === 'crewAi');
                
                if (crewAiConfig && crewAiConfig.result && Array.isArray(crewAiConfig.result.parsed_workflow)) {
                  // 在最新数据中查找当前智能体
                  const workflow = crewAiConfig.result.parsed_workflow.find(
                    (wf: any) => wf && (wf.work_name === (parsedData["work Name"] || parsedData.work_name))
                  );
                  
                  if (workflow) {
                    // 处理tools和agent_mcp_tools字段
                    const crewTools = Array.isArray(workflow.tools) 
                      ? workflow.tools 
                      : (typeof workflow.tools === 'string' ? workflow.tools.split(',').map((t: string) => t.trim()).filter(Boolean) : []);
                    
                    const mcpTools = Array.isArray(workflow.agent_mcp_tools)
                      ? workflow.agent_mcp_tools
                      : (typeof workflow.agent_mcp_tools === 'string' ? workflow.agent_mcp_tools.split(',').map((t: string) => t.trim()).filter(Boolean) : []);
                    
                    // 使用最新的数据
                    console.log("使用API获取的最新数据:", workflow);
                    setSelectedDetail({
                      type: 'agent',
                      name: workflow.agent_role || '未知角色',
                      goal: workflow.agent_goal || '未知目标',
                      backstory: workflow.agent_backstory || '未知背景',
                      task_description: workflow.task_description || '未知任务描述',
                      task_expected_output: workflow.task_expected_output || '未知预期输出',
                      work_name: workflow.work_name || '',
                      crewTools: crewTools,
                      mcpTools: mcpTools,
                    });
                    return;
                  }
                }
              }
            } catch (err) {
              console.error("获取最新数据失败，使用传入的数据:", err);
            }
          }
          
          // 如果无法获取最新数据，使用传入的数据，支持带空格和下划线两种格式
          console.log("使用传入的parsed_workflow数据创建详情:", parsedData);
          
          // 字段映射函数，优先使用带空格的字段名，如果不存在则使用下划线格式
          const getFieldValue = (spacedKey: string, underscoreKey: string, defaultValue: string = '') => {
            return parsedData[spacedKey] || parsedData[underscoreKey] || defaultValue;
          };
          
          // 工具字段处理
          const getToolsArray = (spacedKey: string, underscoreKey: string) => {
            const toolsValue = parsedData[spacedKey] || parsedData[underscoreKey];
            if (Array.isArray(toolsValue)) {
              return toolsValue;
            } else if (typeof toolsValue === 'string' && toolsValue.trim()) {
              return toolsValue.split(',').map(t => t.trim()).filter(Boolean);
            }
            return [];
          };
          
          setSelectedDetail({
            type: 'agent',
            name: getFieldValue("agent Role", "agent_role", "未知角色"),
            goal: getFieldValue("agent Goal", "agent_goal", "未知目标"),
            backstory: getFieldValue("agent Backstory", "agent_backstory", "未知背景"),
            task_description: getFieldValue("task Description", "task_description", "未知任务描述"),
            task_expected_output: getFieldValue("task Expected Output", "task_expected_output", "未知预期输出"),
            work_name: getFieldValue("work Name", "work_name", ""),
            crewTools: getToolsArray("tools", "tools"),
            mcpTools: getToolsArray("agent Mcp Tools", "agent_mcp_tools"),
          });
          return;
        }
      } catch (e) {
        console.error('解析JSON数据失败:', e);
      }

      // 如果尝试解析JSON失败，尝试使用原有的解析逻辑（这部分代码可能不会再被执行，但为了兼容性保留）
      console.log("尝试使用原有的parseAgentString解析数据");
      const agentConfig = parseAgentString(data);
      console.log("parseAgentString解析结果:", agentConfig);
      
      setSelectedDetail({
        type: 'agent',
        name: agentConfig.name || '未知名称',
        goal: agentConfig.goal || '未知目标',
        backstory: agentConfig.backstory || '未知背景',
        task_description: '未提供',
        task_expected_output: '未提供',
        work_name: '',
        crewTools: agentConfig.tools && agentConfig.tools?.split(',') || [],
        mcpTools: agentConfig.agent_mcp_tools && agentConfig.agent_mcp_tools?.split(',') || [],
      });
    } catch (error) {
      console.error('解析详情数据失败:', error);
      // 提供默认值，避免显示错误
      setSelectedDetail({
        type: 'agent',
        name: '解析错误',
        goal: '无法解析目标信息',
        backstory: '无法解析背景信息',
        task_description: '无法解析任务描述',
        task_expected_output: '无法解析预期输出',
        work_name: ''
      });
    }
  };

  const renderRequirementTable = () => {
    // 从需求文档中获取文本内容
    const requirementText = getDocContent('Requirement');
    
    // 如果内容不存在或不完整，不显示表格
    if (!requirementText || !requirementText.includes('| 需求描述') || !requirementText.includes('| 优先级')) {
      return null;
    }
    
    // 传递完整的Markdown文本给表格组件
    return <RequirementTable markdownText={requirementText} />;
  };

  // 添加处理编辑的函数
  const handleEdit = () => {
    if (selectedDetail) {
      setEditedDetail({...selectedDetail, isEditing: true});
    }
  };

  // 添加处理取消的函数
  const handleCancel = () => {
    setEditedDetail(null);
  };

  // 添加处理保存的函数
  const handleSave = async () => {
    if (!editedDetail || !appId) return;
    
    try {
      setIsSaving(true);
      const token = localStorage.getItem('token');
      
      // 获取当前所有的parsed_workflow数据
      const allWorkflows = getParsedWorkflow();
      
      // 找到当前正在编辑的工作流，如果找到则更新，否则添加新的
      let updatedWorkflows = [...allWorkflows];
      const indexToUpdate = updatedWorkflows.findIndex(
        workflow => workflow.work_name === editedDetail.work_name
      );
      
      // 保留原始的work_description和work_detailed字段或提供默认值
      const originalWorkflow = indexToUpdate >= 0 ? updatedWorkflows[indexToUpdate] : null;
      
      const updatedWorkflow = {
        work_name: editedDetail.work_name || '',
        work_description: originalWorkflow?.work_description || '',
        work_detailed: originalWorkflow?.work_detailed || originalWorkflow?.detailed || '',  // 使用work_detailed字段，同时兼容detailed
        agent_role: editedDetail.name || '',
        agent_goal: editedDetail.goal || '',
        agent_backstory: editedDetail.backstory || '',
        task_description: editedDetail.task_description || '',
        task_expected_output: editedDetail.task_expected_output || '',
        tools: editedDetail?.crewTools?.join(",") || "",
        agent_mcp_tools: editedDetail?.mcpTools?.join(",") || ""
      };
      
      if (indexToUpdate >= 0) {
        // 更新现有的工作流
        updatedWorkflows[indexToUpdate] = updatedWorkflow;
      } else {
        // 添加新的工作流
        updatedWorkflows.push(updatedWorkflow);
      }

      // 提交时将tools和agent_mcp_tools转为字符串
      const submitWorkflows = updatedWorkflows.map(wf => ({
        ...wf,
        tools: Array.isArray(wf.tools) ? wf.tools.join(',') : (wf.tools || ''),
        agent_mcp_tools: Array.isArray(wf.agent_mcp_tools) ? wf.agent_mcp_tools.join(',') : (wf.agent_mcp_tools || '')
      }));
      await axios.put(
        `https://ceshi.lightark.cc/api/v1/apps/${appId}/update_crew_ai_workflow`,
        {
          parsed_workflow: submitWorkflows
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // 显示成功消息
      message.success(t('apps.detail.agentDetails.saveSuccess', '智能体信息更新成功'));
      
      // 重新加载所有数据
      await fetchTaskStatus();
      await fetchAppDetail();
      
      // 更新成功后更新当前状态
      setEditedDetail(null);
      
      // 更新内存中的工作流数据，确保本地状态是最新的
      if (taskStatus && taskStatus.parsed_workflow) {
        const updatedTaskStatus = { ...taskStatus };
        
        if (indexToUpdate >= 0 && updatedTaskStatus.parsed_workflow) {
          updatedTaskStatus.parsed_workflow[indexToUpdate] = updatedWorkflow;
        } else if (updatedTaskStatus.parsed_workflow) {
          updatedTaskStatus.parsed_workflow.push(updatedWorkflow);
        }
        
        setTaskStatus(updatedTaskStatus);
      }
      
      // 更新当前选中的详情
      setSelectedDetail({
        type: 'agent',
        name: updatedWorkflow.agent_role,
        goal: updatedWorkflow.agent_goal,
        backstory: updatedWorkflow.agent_backstory,
        task_description: updatedWorkflow.task_description,
        task_expected_output: updatedWorkflow.task_expected_output,
        work_name: updatedWorkflow.work_name,
        crewTools: (editedDetail.crewTools || []),
        mcpTools: (editedDetail.mcpTools || [])
      });
      
      // 等待一小段时间确保数据已刷新
      setTimeout(() => {
        // 关闭详情页面（可选）
        // setSelectedDetail(null);
      }, 1000);
    } catch (error) {
      console.error('更新智能体信息失败:', error);
      message.error(t('apps.detail.agentDetails.saveFailed', '更新智能体信息失败，请重试'));
    } finally {
      setIsSaving(false);
    }
  };

  // 添加切换调度类型的函数
  const handleToggleScheduleType = async () => {
    if (!appId || isUpdatingScheduleType) return;
    
    try {
      setIsUpdatingScheduleType(true);
      const token = localStorage.getItem('token');
      const currentType = getAgentLayoutType();
      const newType = currentType === 'hierarchical' ? 'sequential' : 'hierarchical';
      
      // 调用API更新调度类型
      await axios.put(
        `https://ceshi.lightark.cc/api/v1/apps/${appId}/update_crew_ai_workflow`,
        {
          type: newType
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // 显示成功消息
      const typeText = getScheduleTypeText(newType);
      message.success(t('apps.detail.agentDetails.scheduleType.switchSuccess', '已切换为{{type}}模式', { type: typeText }));
      
      // 重新加载数据以刷新界面
      await fetchTaskStatus();
      await fetchAppDetail();
      
    } catch (error) {
      console.error('切换调度类型失败:', error);
      message.error(t('apps.detail.agentDetails.scheduleType.switchFailed', '切换调度类型失败，请重试'));
    } finally {
      setIsUpdatingScheduleType(false);
    }
  };
  // 2. 编辑时拉取MCP工具（如有后端代理，建议用/api/mcpservers）
  const fetchMcpTools = useCallback(async () => {
    setLoadingMcpTools(true);
    try {
      // 建议后端代理解决跨域，如/api/mcpservers
      const res = await fetch('/mcpservers?enabled=true&available=true');
      const data = await res.json();
      // 只保留MCP Server本身，不展开tool_list
      const servers = data.map((mcp: any) => ({
        mcpId: mcp.mcpId || mcp.id,
        name: mcp.name,
        description: mcp.description
      }));
      setMcpToolsList(servers);
    } catch (e) {
      setMcpToolsList([]);
      // 可在UI中提示："MCP工具列表获取失败，请联系管理员配置后端代理。"
    }
    setLoadingMcpTools(false);
  }, []);

  // 工具编辑弹窗打开
  const handleOpenToolsModal = (workflow: any, index: number) => {
    setToolsModal({
      open: true,
      workflowIndex: index,
      crewTools: workflow.tools ? [...workflow.tools] : [],
      mcpTools: workflow.agent_mcp_tools ? [...workflow.agent_mcp_tools] : [],
      saving: false
    });
  };
  // 工具编辑弹窗关闭
  const handleCloseToolsModal = () => {
    setToolsModal({ open: false, workflowIndex: null, crewTools: [], mcpTools: [], saving: false });
  };
  // 工具编辑弹窗保存
  const handleSaveTools = async () => {
    if (toolsModal.workflowIndex == null || !appId) return;
    setToolsModal((prev) => ({ ...prev, saving: true }));
    try {
      const allWorkflows = getParsedWorkflow();
      const updatedWorkflows = allWorkflows.map((w, i) =>
        i === toolsModal.workflowIndex
          ? {
              ...w,
              tools: toolsModal.crewTools,
              agent_mcp_tools: toolsModal.mcpTools
            }
          : w
      );
      const token = localStorage.getItem('token');
      await axios.put(
        `https://ceshi.lightark.cc/api/v1/apps/${appId}/update_crew_ai_workflow`,
        { parsed_workflow: updatedWorkflows },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      message.success('Tools已保存');
      await fetchTaskStatus();
      await fetchAppDetail();
      handleCloseToolsModal();
    } catch (e) {
      message.error('Tools保存失败');
      setToolsModal((prev) => ({ ...prev, saving: false }));
    }
  };
  // 工具输入变更
  const handleToolsChange = (type: 'crew' | 'mcp', value: string) => {
    const arr = value.split(',').map(s => s.trim()).filter(Boolean);
    setToolsModal(prev => 
      type === 'crew' 
        ? { ...prev, crewTools: arr }
        : { ...prev, mcpTools: arr }
    );
  };

  useEffect(() => {
    if (appId) {
      fetchTaskStatus();
      fetchAppDetail();
    }
  }, [appId, fetchTaskStatus, fetchAppDetail]);

  // 3. 进入编辑时初始化MCP工具列表
  useEffect(() => {
    // 只在editedDetail?.isEditing为true时拉取
    fetchMcpTools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * 处理预览生成
   */
  const handleGeneratePreview = async () => {
    if (!webHtmlCode.trim()) {
      setPreviewError('没有可预览的代码');
      return;
    }

    setIsGeneratingPreview(true);
    setPreviewError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://ceshi.lightark.cc/api/v1/apps/generate_temp_html', {
        method: 'POST',
        headers: {
          'authorization': `Bearer ${token}`,
          'Content-Type': 'application/xml'
        },
        body: webHtmlCode
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: PreviewResponse = await response.json();
      const previewUrl = `https://ceshi.lightark.cc/web/${result.filename}`;
      
      setPreviewUrl(previewUrl);
      setIsPreviewMode(true);
      // 切换到预览模式并打开预览模态框
      setWebHtmlViewMode('preview');
      setIsPreviewModalOpen(true);

    } catch (error) {
      setPreviewError('生成预览失败，请重试');
      console.error('预览生成失败:', error);
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  /**
   * 退出预览模式
   */
  const handleExitPreview = () => {
    setIsPreviewMode(false);
    setPreviewUrl('');
    setWebHtmlViewMode('code');
    setIsPreviewModalOpen(false);
  };

  /**
   * 切换视图模式（代码/预览）
   */
  const handleToggleViewMode = (mode: 'code' | 'preview') => {
    if (mode === 'preview' && !isPreviewMode) {
      // 如果要切换到预览模式但还没有预览，先生成预览
      handleGeneratePreview();
    } else {
      setWebHtmlViewMode(mode);
    }
  };

  /**
   * 打开预览模态框
   */
  const handleOpenPreviewModal = () => {
    if (isPreviewMode) {
      setIsPreviewModalOpen(true);
    }
  };

  /**
   * 关闭预览模态框
   */
  const handleClosePreviewModal = () => {
    setIsPreviewModalOpen(false);
  };

  if (!taskStatus) return <div className="loading">{t('apps.loading', '加载中...')}</div>;

  // 添加调试日志
  console.log('完整的taskStatus:', taskStatus);
  console.log('节点信息:', taskStatus.nodes);
  
  // 遍历所有节点，检查文档内容
  Object.entries(taskStatus.nodes || {}).forEach(([key, node]) => {
    console.log(`节点 ${key} 的内容:`, node?.text);
  });

  const formatName = (name: string): string => {
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
      .replace(/:$/, ''); // 移除末尾的冒号
  };

  // 获取智能体排列类型（从API配置中获取）
  const getAgentLayoutType = (): string => {
    console.log("=== 开始获取智能体排列类型 ===");
    console.log("appDetail:", appDetail);
    console.log("taskStatus:", taskStatus);
    
    // 先尝试从appDetail中获取
    if (appDetail?.app_configs) {
      console.log("appDetail.app_configs:", appDetail.app_configs);
      const configs = Array.isArray(appDetail.app_configs) 
        ? appDetail.app_configs 
        : Object.values(appDetail.app_configs);
      
      console.log("处理后的configs:", configs);
      const crewAiConfig = configs.find((config: any) => config.api_name === 'crewAi');
      console.log("找到的crewAiConfig:", crewAiConfig);
      
      if (crewAiConfig?.result?.type) {
        console.log("从appDetail获取到的排列类型:", crewAiConfig.result.type);
        return crewAiConfig.result.type;
      }
    }
    
    // 如果appDetail中没有，尝试从taskStatus中获取
    if (taskStatus?.nodes?.crewAi?.outputs?.type) {
      console.log("从taskStatus获取到的排列类型:", taskStatus.nodes.crewAi.outputs.type);
      return taskStatus.nodes.crewAi.outputs.type;
    }
    
    // 默认返回sequential
    console.log("未找到排列类型配置，使用默认值: sequential");
    console.log("=== 结束获取智能体排列类型 ===");
    return 'sequential';
  };

  // 获取调度类型的显示文本
  const getScheduleTypeText = (type: string): string => {
    return type === 'hierarchical' 
      ? t('apps.detail.agentDetails.scheduleType.hierarchical', '工作流')
      : t('apps.detail.agentDetails.scheduleType.sequential', '自主调度');
  };

  // 获取切换按钮的文本
  const getToggleButtonText = (currentType: string): string => {
    return currentType === 'hierarchical' 
      ? t('apps.detail.agentDetails.scheduleType.switchToSequential', '切换为自主调度')
      : t('apps.detail.agentDetails.scheduleType.switchToHierarchical', '切换为工作流');
  };

  // 根据智能体数量动态计算容器高度
  const getContainerHeight = (totalAgents: number): number => {
    const layoutType = getAgentLayoutType();
    
    if (layoutType === 'hierarchical') {
      // hierarchical布局需要更高的容器来容纳垂直排列的节点
      if (totalAgents <= 3) return 500;
      if (totalAgents === 4) return 600;
      if (totalAgents === 5) return 700;
      if (totalAgents === 6) return 800;
      if (totalAgents === 7) return 900;
      if (totalAgents === 8) return 1000;
      // 超过8个节点时，按每个节点150px的高度计算
      return Math.max(1000, totalAgents * 150 + 200);
    }
    
    // sequential布局保持原有的500px高度
    return 500;
  };

  // 获取固定的智能体坐标位置
  const getAgentPositions = (totalAgents: number): Array<{left: number, top: number}> => {
    const centerX = 250;
    const centerY = 250;
    const nodeSize = 60; // 节点大小的一半
    const layoutType = getAgentLayoutType();
    
    // 如果是hierarchical类型，使用竖线排列
    if (layoutType === 'hierarchical') {
      // 根据节点数量动态调整间距，确保所有节点都在容器范围内
      let spacing = 160; // 默认间距
      let startY = 0; // 起始Y坐标
      
      if (totalAgents === 5) {
        // 5个智能体时，移动到方框最上面，保持一些间隙
        spacing = 140; // 稍微紧凑一些的间距
        startY = 80; // 从方框顶部开始，留出一些间隙
      } else if (totalAgents >= 6) {
        // 6个或更多智能体时，动态计算间距
        const maxHeight = getContainerHeight(totalAgents) - 120; // 减去节点高度的缓冲
        const availableHeight = maxHeight - 100; // 留出上下边距
        spacing = Math.max(120, Math.min(160, availableHeight / (totalAgents - 1)));
        
        // 计算起始Y坐标，确保垂直居中
        const totalHeight = (totalAgents - 1) * spacing;
        const containerHeight = getContainerHeight(totalAgents);
        startY = (containerHeight - totalHeight) / 2;
      } else if (totalAgents === 4) {
        // 5个智能体时，移动到方框最上面，保持一些间隙
        spacing = 150; // 稍微紧凑一些的间距
        startY = 20; // 从方框顶部开始，留出一些间隙
      } else {
        // 4个或以下智能体时，保持垂直居中
        const totalHeight = (totalAgents - 1) * spacing;
        const containerHeight = getContainerHeight(totalAgents);
        startY = (containerHeight - totalHeight) / 2;
      }
      
      // 调整X坐标，使节点更好地与容器中线对齐
      const nodeWidth = 120; // 节点的实际宽度
      const alignedX = centerX - nodeWidth / 2; // 使节点中心与容器中心对齐
      
      return Array.from({ length: totalAgents }, (_, i) => ({
        left: alignedX - 15, // 所有智能体在同一条竖线上，与中线对齐
        top: startY + i * spacing - nodeSize
      }));
    }
    
    // 如果是sequential类型或其他类型，使用原有的排列方式
    switch (totalAgents) {
      case 1:
        return [
          { left: centerX - nodeSize, top: centerY - nodeSize }
        ];
      
      case 2:
        return [
          { left: centerX - 120 - nodeSize, top: centerY - nodeSize },
          { left: centerX + 120 - nodeSize, top: centerY - nodeSize }
        ];
      
      case 3:
        return [
          { left: centerX - nodeSize, top: centerY - 200 - nodeSize }, // 上
          { left: centerX - 200 - nodeSize, top: centerY + 100 - nodeSize }, // 左下
          { left: centerX + 200 - nodeSize, top: centerY + 100 - nodeSize }  // 右下
        ];
      
      case 4:
        return [
          { left: centerX - nodeSize, top: centerY - 200 - nodeSize }, // 上
          { left: centerX + 200 - nodeSize, top: centerY - nodeSize }, // 右
          { left: centerX - nodeSize, top: centerY + 200 - nodeSize }, // 下
          { left: centerX - 200 - nodeSize, top: centerY - nodeSize }  // 左
        ];
      
      case 5:
        return [
          { left: centerX - nodeSize, top: centerY - 200 - nodeSize }, // 上方中心
          { left: centerX + 200 - nodeSize, top: centerY - 46 - nodeSize }, // 右上
          { left: centerX + 120 - nodeSize, top: centerY + 200 - nodeSize }, // 右下
          { left: centerX - 120 - nodeSize, top: centerY + 200 - nodeSize }, // 左下
          { left: centerX - 200 - nodeSize, top: centerY - 46 - nodeSize }  // 左上
        ];
      
      case 6:
        return [
          { left: centerX - nodeSize, top: centerY - 200 - nodeSize }, // 上
          { left: centerX + 170 - nodeSize, top: centerY - 100 - nodeSize }, // 右上
          { left: centerX + 170 - nodeSize, top: centerY + 100 - nodeSize }, // 右下
          { left: centerX - nodeSize, top: centerY + 200 - nodeSize }, // 下
          { left: centerX - 170 - nodeSize, top: centerY + 100 - nodeSize }, // 左下
          { left: centerX - 170 - nodeSize, top: centerY - 100 - nodeSize }  // 左上
        ];
      
      default:
        // 如果超过6个，回退到圆形布局
        const radius = Math.max(120, 20 * totalAgents);
        return Array.from({ length: totalAgents }, (_, i) => {
          const angle = (i / totalAgents) * 2 * Math.PI - Math.PI / 2;
          return {
            left: centerX + radius * Math.cos(angle) - nodeSize,
            top: centerY + radius * Math.sin(angle) - nodeSize
          };
        });
    }
  };

  // 添加渲染智能体连接线的函数
  const renderAgentConnections = () => {
    const workflows = getParsedWorkflow().filter((w: any) => w && w.agent_role);
    const agentCount = workflows.length;
    
    if (agentCount < 2) {
      return null;
    }

    const connections = [];
    const layoutType = getAgentLayoutType();
    
    // 如果是hierarchical类型，使用竖线连接
    if (layoutType === 'hierarchical') {
      const positions = getAgentPositions(agentCount);
      
      // 为4个节点的hierarchical布局单独配置连线
      if (agentCount === 4) {
        // 第1个节点到第2个节点的连线
        connections.push(
          <path
            key="hierarchical-connection-0"
            d={`M ${positions[0].left + 75} ${positions[0].top + 135} L ${positions[1].left + 75} ${positions[1].top+20}`}
            stroke="#1a73e8"
            strokeWidth="3"
            fill="none"
            markerEnd="url(#arrowhead-0)"
            opacity="1"
          />
        );
        
        // 第2个节点到第3个节点的连线
        connections.push(
          <path
            key="hierarchical-connection-1"
            d={`M ${positions[1].left + 75} ${positions[1].top + 165} L ${positions[2].left + 75} ${positions[2].top+50}`}
            stroke="#1a73e8"
            strokeWidth="3"
            fill="none"
            markerEnd="url(#arrowhead-1)"
            opacity="1"
          />
        );
        
        // 第3个节点到第4个节点的连线
        connections.push(
          <path
            key="hierarchical-connection-2"
            d={`M ${positions[2].left + 75} ${positions[2].top + 195} L ${positions[3].left + 75} ${positions[3].top+80}`}
            stroke="#1a73e8"
            strokeWidth="3"
            fill="none"
            markerEnd="url(#arrowhead-2)"
            opacity="1"
          />
        );
        
        return connections;
      }
      
      // 其他数量的hierarchical布局继续使用通用逻辑
      const nodeSize = 60; // 节点半径
      const nodeRadius = 60; // 实际节点的半径（从CSS中的120px宽度/2得出）
      
      // 从上到下依次连接
      for (let i = 0; i < agentCount - 1; i++) {
        const currentPos = positions[i];
        const nextPos = positions[i + 1];
        
        // 计算节点的真实中心点（考虑到节点的实际大小）
        // 节点宽度为120px，所以中心点在left + 60的位置
        const nodeWidth = 120;
        const currentCenterX = currentPos.left+15 + nodeWidth / 2;
        const currentCenterY = currentPos.top + nodeSize;
        const nextCenterX = nextPos.left+15 + nodeWidth / 2;
        const nextCenterY = nextPos.top + nodeSize;
        
        // 直线连接，从当前节点底部边缘到下一个节点顶部边缘
        // 节点实际半径约为60px，所以连接点应该在节点边缘
        const startY = currentCenterY + nodeRadius;
        const endY = nextCenterY - nodeRadius;
        
        connections.push(
          <path
            key={`hierarchical-connection-${i}`}
            d={`M ${currentCenterX} ${startY} L ${nextCenterX} ${endY}`}
            stroke="#1a73e8"
            strokeWidth="3"
            fill="none"
            markerEnd={`url(#arrowhead-${i})`}
            opacity="1"
          />
        );
      }
      
      return connections;
    }
    
    // 如果是sequential类型，使用原有的连接方式
    // 根据节点数量写死连接线路径
    switch (agentCount) {
      case 2:
        // 2个节点：双向箭头连接，形成循环
        connections.push(
          // 第一条线：从左到右
          <path
            key="connection-0"
            d="M 310 230 Q 265 210 220 230"
            stroke="#1a73e8"
            strokeWidth="4"
            fill="none"
            markerEnd="url(#arrowhead-0)"
            opacity="1"
          />,
          // 第二条线：从右到左
          <path
            key="connection-1"
            d="M 220 270 Q 265 290 310 270"
            stroke="#1a73e8"
            strokeWidth="4"
            fill="none"
            markerEnd="url(#arrowhead-1)"
            opacity="1"
          />
        );
        break;
        
      case 3:
        // 3个节点：三角形连接 - 弧线连接，弧度朝外
        connections.push(
          // 上 -> 左下 (弧度朝外)
          <path
            key="connection-0"
            d="M 210 110 Q 130 180 80 290"
            stroke="#1a73e8"
            strokeWidth="4"
            fill="none"
            markerEnd="url(#arrowhead-0)"
            opacity="1"
          />,
          // 左下 -> 右下 (弧度朝外)
          <path
            key="connection-1"
            d="M 110 350 Q 250 400 390 350"
            stroke="#1a73e8"
            strokeWidth="4"
            fill="none"
            markerEnd="url(#arrowhead-1)"
            opacity="1"
          />,
          // 右下 -> 上 (弧度朝外)
          <path
            key="connection-2"
            d="M 450 290 Q 370 180 290 110"
            stroke="#1a73e8"
            strokeWidth="4"
            fill="none"
            markerEnd="url(#arrowhead-2)"
            opacity="1"
          />
        );
        break;
        
      case 4:
        // 4个节点：十字形连接 - 弧线连接，弧度朝外
        connections.push(
          // 上 -> 右 (弧度朝外)
          <path
            key="connection-0"
            d="M 340 40 Q 420 80 460 180"
            stroke="#1a73e8"
            strokeWidth="4"
            fill="none"
            markerEnd="url(#arrowhead-0)"
            opacity="1"
          />,
          // 右 -> 下 (弧度朝外)
          <path
            key="connection-1"
            d="M 460 320 Q 420 420 350 460"
            stroke="#1a73e8"
            strokeWidth="4"
            fill="none"
            markerEnd="url(#arrowhead-1)"
            opacity="1"
          />,
          // 下 -> 左 (弧度朝外)
          <path
            key="connection-2"
            d="M 180 460 Q 80 420 70 320"
            stroke="#1a73e8"
            strokeWidth="4"
            fill="none"
            markerEnd="url(#arrowhead-2)"
            opacity="1"
          />,
          // 左 -> 上 (弧度朝外)
          <path
            key="connection-3"
            d="M 70 180 Q 80 80 180 40"
            stroke="#1a73e8"
            strokeWidth="4"
            fill="none"
            markerEnd="url(#arrowhead-3)"
            opacity="1"
          />
        );
        break;
        
      case 5:
        // 5个节点：正五边形连接 - 弧线连接，弧度朝外
        connections.push(
          // 上 -> 右上 (弧度朝外)
          <path
            key="connection-0"
            d="M 340 50 Q 400 80 460 140"
            stroke="#1a73e8"
            strokeWidth="4"
            fill="none"
            markerEnd="url(#arrowhead-0)"
            opacity="1"
          />,
          // 右上 -> 右下 (弧度朝外)
          <path
            key="connection-1"
            d="M 460 264 Q 440 330 390 390"
            stroke="#1a73e8"
            strokeWidth="4"
            fill="none"
            markerEnd="url(#arrowhead-1)"
            opacity="1"
          />,
          // 右下 -> 左下 (弧度朝外)
          <path
            key="connection-2"
            d="M 310 450 Q 250 460 220 450"
            stroke="#1a73e8"
            strokeWidth="4"
            fill="none"
            markerEnd="url(#arrowhead-2)"
            opacity="1"
          />,
          // 左下 -> 左上 (弧度朝外)
          <path
            key="connection-3"
            d="M 140 390 Q 90 330 70 264"
            stroke="#1a73e8"
            strokeWidth="4"
            fill="none"
            markerEnd="url(#arrowhead-3)"
            opacity="1"
          />,
          // 左上 -> 上 (弧度朝外)
          <path
            key="connection-4"
            d="M 70 145 Q 120 80 190 50"
            stroke="#1a73e8"
            strokeWidth="4"
            fill="none"
            markerEnd="url(#arrowhead-4)"
            opacity="1"
          />
        );
        break;
        
      case 6:
        // 6个节点：正六边形连接 - 弧线连接，弧度朝外
        connections.push(
          // 上 -> 右上 (弧度朝外)
          <path
            key="connection-0"
            d="M 340 50 Q 400 70 420 90"
            stroke="#1a73e8"
            strokeWidth="4"
            fill="none"
            markerEnd="url(#arrowhead-0)"
            opacity="1"
          />,
          // 右上 -> 右下 (弧度朝外)
          <path
            key="connection-1"
            d="M 450 210 Q 459 250 450 290"
            stroke="#1a73e8"
            strokeWidth="4"
            fill="none"
            markerEnd="url(#arrowhead-1)"
            opacity="1"
          />,
          // 右下 -> 下 (弧度朝外)
          <path
            key="connection-2"
            d="M 420 410 Q 370 450 340 450"
            stroke="#1a73e8"
            strokeWidth="4"
            fill="none"
            markerEnd="url(#arrowhead-2)"
            opacity="1"
          />,
          // 下 -> 左下 (弧度朝外)
          <path
            key="connection-3"
            d="M 190 450 Q 170 420 110 410"
            stroke="#1a73e8"
            strokeWidth="4"
            fill="none"
            markerEnd="url(#arrowhead-3)"
            opacity="1"
          />,
          // 左下 -> 左上 (弧度朝外)
          <path
            key="connection-4"
            d="M 80 290 Q 70 250 80 210"
            stroke="#1a73e8"
            strokeWidth="4"
            fill="none"
            markerEnd="url(#arrowhead-4)"
            opacity="1"
          />,
          // 左上 -> 上 (弧度朝外)
          <path
            key="connection-5"
            d="M 100 90 Q 130 70 190 50"
            stroke="#1a73e8"
            strokeWidth="4"
            fill="none"
            markerEnd="url(#arrowhead-5)"
            opacity="1"
          />
        );
        break;
        
      default:
        // 超过6个节点时回退到动态计算
        const positions = getAgentPositions(agentCount);
        const nodeSize = 60;

        for (let i = 0; i < agentCount; i++) {
          const currentPos = positions[i];
          const nextPos = positions[(i + 1) % agentCount];
          
          const currentCenterX = currentPos.left + nodeSize;
          const currentCenterY = currentPos.top + nodeSize;
          const nextCenterX = nextPos.left + nodeSize;
          const nextCenterY = nextPos.top + nodeSize;
          
          // 直线连接，不使用弧线
          const pathData = `M ${currentCenterX} ${currentCenterY} L ${nextCenterX} ${nextCenterY}`;
          
          connections.push(
            <path
              key={`connection-${i}`}
              d={pathData}
              stroke="#1a73e8"
              strokeWidth="4"
              fill="none"
              markerEnd={`url(#arrowhead-${i})`}
              opacity="1"
            />
          );
        }
        break;
    }
    
    return connections;
  };

  return (
    <div className="task-progress-container">
      {error && <div className="error-message">{error}</div>}
      <div className="progress-panel">
        <div className="progress-tabs">
          <div className="progress-timeline">
            {[
              { api_name: 'Requirement' },
              { api_name: 'User Stories' },
              { api_name: 'Specification' },
              { api_name: 'web_html' },
              { api_name: 'crewAi' }
            ].map((item, index, arr) => {
              // 查找对应的执行状态
              const status = taskStatus.execution_statuses.find(s => s.api_name === item.api_name);
              
              // 对于web_html，即使没有执行状态也要显示，创建一个默认状态
              const displayStatus = status || (item.api_name === 'web_html' ? {
                api_name: 'web_html',
                status: 'completed', // 假设已完成，因为app_configs中有数据
                progress: 100,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              } : null);
              
              if (!displayStatus) return null;
              
              return (
                <div key={displayStatus.api_name} className="timeline-item">
                  <div 
                    className={`progress-tab ${selectedTab === displayStatus.api_name ? 'active' : ''}`}
                    onClick={() => setSelectedTab(displayStatus.api_name)}
                    data-tab={displayStatus.api_name}
                  >
                    <div className={`tab-icon-wrapper ${displayStatus.status === 'completed' ? 'completed' : ''}`}>
                      {/* 已完成：绿色对勾 */}
                      {displayStatus.status === 'completed' ? (
                        <svg className="check-icon" viewBox="0 0 24 24">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                        </svg>
                      ) : (
                        // 所有未完成的都显示转圈动画
                        <div className="custom-spinner" />
                      )}
                    </div>
                    <span className="tab-name">{getStatusName(displayStatus.api_name)}</span>
                    
                    {/* 按钮组容器 */}
                    {shouldShowDocIcon(displayStatus) && (
                      <div className="action-buttons-container">
                        <button 
                          className="doc-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDocClick(displayStatus);
                          }}
                          title="查看文档"
                        >
                          <svg 
                            className="doc-icon" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2"
                          >
                            {displayStatus.api_name === 'Requirement' && (
                              <>
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="16" y1="13" x2="8" y2="13" />
                                <line x1="16" y1="17" x2="8" y2="17" />
                                <polyline points="10 9 9 9 8 9" />
                              </>
                            )}
                            {displayStatus.api_name === 'User Stories' && (
                              <>
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="16" y1="13" x2="8" y2="13" />
                                <line x1="16" y1="17" x2="8" y2="17" />
                                <line x1="10" y1="9" x2="8" y2="9" />
                              </>
                            )}
                            {displayStatus.api_name === 'Specification' && (
                              <>
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="16" y1="13" x2="8" y2="13" />
                                <line x1="16" y1="17" x2="8" y2="17" />
                                <line x1="10" y1="9" x2="8" y2="9" />
                              </>
                            )}
                            {displayStatus.api_name === 'web_html' && (
                              <>
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                <line x1="9" y1="9" x2="15" y2="9"/>
                                <line x1="9" y1="13" x2="15" y2="13"/>
                                <line x1="9" y1="17" x2="13" y2="17"/>
                              </>
                            )}
                          </svg>
                        </button>
                        
                        {handleExportPDF && (
                          <button 
                            className="export-button-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              const exportType = getExportType(displayStatus.api_name);
                              if (exportType && handleExportPDF) {
                                handleExportPDF(exportType);
                              }
                            }}
                            title={`导出${getStatusName(displayStatus.api_name)}`}
                          >
                            <svg 
                              className="export-icon" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              stroke="currentColor" 
                              strokeWidth="2"
                            >
                              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
                            </svg>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {index < arr.length - 1 && <div className="timeline-line" />}
                </div>
              );
            })}
          </div>
        </div>
        
        <div className="progress-content">
          {selectedTab === 'crewAi' && (
            <div className="metadata-content crew-ai-content">
              {getParsedWorkflow().length > 0 ? (
                <div className="workflow-diagram">
                  <div className="workflow-header">
                    <h3 className="workflow-title">{t('apps.detail.agentDetails.workflowTitle', '多智能体流程')}</h3>
                    <div className="schedule-toggle-container">
                      <button 
                        className="schedule-toggle-button"
                        onClick={handleToggleScheduleType}
                        disabled={isUpdatingScheduleType}
                        title={getToggleButtonText(getAgentLayoutType())}
                      >
                        <span className="schedule-type-text">
                          {isUpdatingScheduleType 
                            ? t('apps.detail.agentDetails.scheduleType.switching', '切换中...') 
                            : getScheduleTypeText(getAgentLayoutType())
                          }
                        </span>
                        <svg 
                          className={`toggle-icon ${isUpdatingScheduleType ? 'rotating' : ''}`}
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2"
                        >
                          <path d="M1 4v6h6M23 20v-6h-6"/>
                          <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className={`workflow-container ${getAgentLayoutType() === 'hierarchical' ? 'hierarchical' : ''} ${getAgentLayoutType() === 'hierarchical' ? `agents-${getParsedWorkflow().filter(w => w && w.agent_role).length}` : ''}`}>
                    {/* 开始节点 - 用户 */}
                    <div className="workflow-node start-node">
                      <div className="node-icon user-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                      </div>
                      <div className="node-text">{t('apps.detail.agentDetails.user', '用户')}</div>
                    </div>
                    
                    {/* 连接箭头 - 输入要求 */}
                    <div className="workflow-arrow down-arrow arrow-to-process">
                      <div className="arrow-line"></div>
                    </div>
                    
                    {/* 智能体处理区域 - 大框 */}
                    <div className="agents-process-area">
                      
                      {/* 智能体节点容器 - 环形布局 */}
                      <div className="agents-circle-container">
                        {/* 智能体之间的连接线 */}
                        <svg 
                          className="agent-connections" 
                          viewBox={`0 0 500 ${getContainerHeight(getParsedWorkflow().filter(w => w && w.agent_role).length)}`} 
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <defs>
                            {Array.from({ length: 10 }, (_, i) => (
                              <marker 
                                key={`arrowhead-${i}`}
                                id={`arrowhead-${i}`} 
                                markerWidth="4" 
                                markerHeight="6" 
                                refX="3.5" 
                                refY="3" 
                                orient="auto" 
                                markerUnits="strokeWidth"
                              >
                                <polygon points="0 0, 4 3, 0 6" fill="#1a73e8" stroke="#1a73e8" strokeWidth="0.5" />
                              </marker>
                            ))}
                          </defs>
                          {renderAgentConnections()}
                        </svg>
                        
                        {getParsedWorkflow().map((workflow, index) => {
                          if (!workflow || !workflow.agent_role) {
                            return null;
                          }
                          // 获取所有有效的智能体
                          const validWorkflows = getParsedWorkflow().filter(w => w && w.agent_role);
                          const totalAgents = validWorkflows.length;
                          const positions = getAgentPositions(totalAgents);
                          const position = positions[index] || { left: 250, top: 250 };
                          const style = {
                            position: 'absolute',
                            left: `${position.left}px`,
                            top: `${position.top}px`,
                            animation: 'none',
                          } as React.CSSProperties;
                          const mcpTools = Array.isArray(workflow.agent_mcp_tools)
                            ? workflow.agent_mcp_tools
                            : (typeof workflow.agent_mcp_tools === 'string' ? workflow.agent_mcp_tools.split(',').map((t: string) => t.trim()).filter(Boolean) : []);
                          const showMcpTag = mcpTools && mcpTools.length > 0;

                          // 取出当前智能体的UI状态
                          const uiState = agentUIState[index] || { showPlus: false, showCodeModal: false, codeInput: '', submitting: false };

                          // 只允许一个代码框
                          const handleOpenCodeModal = (e: React.MouseEvent) => {
                            e.stopPropagation();
                            // 计算"+"按钮的屏幕坐标
                            const btn = plusBtnRef.current[index];
                            if (btn) {
                              const rect = btn.getBoundingClientRect();
                              let top = rect.bottom + 8;
                              let left = rect.left;
                              const modalWidth = 420;
                              if (left + modalWidth > window.innerWidth - 16) {
                                left = window.innerWidth - modalWidth - 16;
                              }
                              const modalHeight = 340;
                              if (top + modalHeight > window.innerHeight - 16) {
                                top = rect.top - modalHeight - 8;
                              }
                              setModalPos(pos => ({ ...pos, [index]: { top, left } }));
                            }
                            // 只保留当前index的showCodeModal为true
                            setAgentUIState(s => {
                              const newState: typeof s = {};
                              Object.keys(s).forEach(k => {
                                newState[+k] = { ...s[+k], showCodeModal: false };
                              });
                              newState[index] = { ...uiState, showCodeModal: true, showPlus: true };
                              return newState;
                            });
                          };

                          // 提交代码逻辑
                          // 添加简单的连接测试函数
                          const testConnection = async () => {
                            try {
                              setDebugInfo('🔍 测试连接中...');
                              const token = localStorage.getItem('token');
                              const response = await axios.get(`https://ceshi.lightark.cc/api/v1/apps/${appId}/detail`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              setDebugInfo('✅ 连接正常！\n服务器响应：' + JSON.stringify(response.data, null, 2).substring(0, 500) + '...');
                            } catch (e: any) {
                              setDebugInfo('❌ 连接失败：' + (e?.message || '未知错误'));
                            }
                          };

                          const handleSubmitCode = async () => {
                            if (!appId) {
                              message.error('应用ID不存在，无法提交代码');
                              setAgentUIState(s => ({ ...s, [index]: { ...uiState, submitting: false } }));
                              return;
                            }
                            
                            setAgentUIState(s => ({ ...s, [index]: { ...uiState, submitting: true } }));
                            setDebugInfo('🚀 开始提交代码...');
                            
                            try {
                              const token = localStorage.getItem('token');
                              setDebugInfo('🔑 获取到token，准备发送请求...');
                              
                              const response = await axios.post(
                                `https://ceshi.lightark.cc/api/v1/apps/${appId}/inject_agent_code`,
                                { 
                                  code: uiState.codeInput,
                                  insert_index: index + 1  // 传递插入位置索引，index从0开始，所以+1表示在第index个agent后插入
                                },
                                {
                                  headers: {
                                    Authorization: `Bearer ${token}`,
                                    'Content-Type': 'application/json'
                                  }
                                }
                              );
                              
                              setDebugInfo('✅ 代码注入成功！服务器响应：' + JSON.stringify(response.data));
                              
                              // 代码注入成功后，刷新应用详情数据
                              setDebugInfo('🔄 开始刷新数据...');
                              await fetchAppDetail();
                              
                              const currentWorkflow = getParsedWorkflow();
                              setDebugInfo(`✅ 数据刷新完成！当前agent数量：${currentWorkflow.length}`);
                              
                              message.success('代码已成功注入，智能体已更新');
                            } catch (e: any) {
                              let errorMsg = '未知错误';
                              if (e?.response) {
                                errorMsg = `HTTP ${e.response.status}: ${JSON.stringify(e.response.data)}`;
                              } else if (e?.message) {
                                errorMsg = e.message;
                              }
                              setDebugInfo('❌ 代码注入失败：' + errorMsg);
                              message.error(`代码注入失败：${errorMsg}`);
                            } finally {
                              setAgentUIState(s => ({ ...s, [index]: { showPlus: false, showCodeModal: false, codeInput: '', submitting: false } }));
                            }
                          };

                          // "+"按钮显示条件
                          const showPlusBtn = uiState.showPlus || uiState.showCodeModal;

                          return (
                            <div
                              key={index}
                              className="workflow-node agent-node circle-node"
                              onClick={e => {
                                if (!(uiState.showCodeModal)) {
                                  handleDetailClick('agent', JSON.stringify(workflow));
                                }
                              }}
                              title={`${t('apps.detail.record.view', '查看详情')}: ${workflow.agent_role}`}
                              style={style}
                              onMouseEnter={() => setAgentUIState(s => ({ ...s, [index]: { ...uiState, showPlus: true } }))}
                              onMouseLeave={() => {
                                if (!uiState.showCodeModal) {
                                  setAgentUIState(s => ({ ...s, [index]: { ...uiState, showPlus: false } }));
                                }
                              }}
                            >
                              <div className="node-icon agent-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                  <circle cx="12" cy="7" r="4" />
                                </svg>
                              </div>
                              <div className="node-text">{formatName(workflow.agent_role)}</div>
                              {showMcpTag && (
                                <div style={{
                                  position: 'absolute',
                                  top: 2,
                                  right: 2,
                                  backgroundColor: 'rgb(255, 84, 138)',
                                  color: 'white',
                                  borderRadius: 4,
                                  padding: '2px 6px',
                                  fontSize: 13,
                                  fontWeight: 600,
                                  zIndex: 10
                                }}>mcp</div>
                              )}
                              {/* "+"按钮 */}
                              {showPlusBtn && (
                                <button
                                  ref={el => plusBtnRef.current[index] = el}
                                  className="agent-plus-btn"
                                  style={{
                                    position: 'absolute',
                                    top: 4,
                                    right: 4,
                                    zIndex: 20,
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    background: '#2563eb',
                                    color: '#fff',
                                    border: 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    boxShadow: '0 2px 8px rgba(37,99,235,0.15)'
                                  }}
                                  onClick={handleOpenCodeModal}
                                  title="注入Python代码"
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                  </svg>
                                </button>
                              )}
                              {/* 代码输入弹窗用Portal渲染到body，fixed定位 */}
                              {uiState.showCodeModal && ReactDOM.createPortal(
                                <div
                                  className="agent-code-modal-overlay"
                                  style={{
                                    position: 'fixed',
                                    top: modalPos[index]?.top ?? 100,
                                    left: modalPos[index]?.left ?? 100,
                                    zIndex: 2000,
                                    background: 'rgba(0,0,0,0.08)',
                                    width: 420,
                                    minWidth: 320,
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                                    borderRadius: 8
                                  }}
                                  onClick={e => { e.stopPropagation(); }}
                                >
                                  <div
                                    className="agent-code-modal"
                                    style={{
                                      background:'#fff',
                                      borderRadius:8,
                                      padding:32,
                                      minWidth:320,
                                      boxShadow:'none',
                                      position:'relative'
                                    }}
                                    onClick={e => { e.stopPropagation(); }}
                                  >
                                    <h3 style={{marginBottom:16}}>注入Python代码</h3>
                                    <textarea
                                      value={uiState.codeInput}
                                      onChange={e => setAgentUIState(s => ({ ...s, [index]: { ...uiState, codeInput: e.target.value } }))}
                                      placeholder="请输入Python代码..."
                                      rows={8}
                                      style={{width:'100%',fontFamily:'monospace',fontSize:15,padding:12,borderRadius:6,border:'1px solid #e0e0e0',resize:'vertical'}}
                                    />
                                    <div style={{marginTop:16,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                                      <button
                                        onClick={e => {
                                          e.stopPropagation();
                                          testConnection();
                                        }}
                                        style={{padding:'6px 12px',borderRadius:4,border:'1px solid #1890ff',background:'#fff',color:'#1890ff',cursor:'pointer',fontSize:'12px'}}
                                      >测试连接</button>
                                      <div style={{display:'flex',gap:12}}>
                                        <button
                                          onClick={e => {
                                            e.stopPropagation();
                                            setAgentUIState(s => ({ ...s, [index]: { ...uiState, showCodeModal: false, showPlus: false, codeInput: '' } }));
                                          }}
                                          style={{padding:'6px 18px',borderRadius:4,border:'none',background:'#eee',color:'#333',cursor:'pointer'}}
                                        >取消</button>
                                        <button
                                          onClick={async e => {
                                            e.stopPropagation();
                                            await handleSubmitCode();
                                          }}
                                          disabled={uiState.submitting||!uiState.codeInput.trim()}
                                          style={{padding:'6px 18px',borderRadius:4,border:'none',background:'#2563eb',color:'#fff',fontWeight:600,cursor:uiState.submitting?'not-allowed':'pointer'}}
                                        >
                                          {uiState.submitting?'提交中...':'提交'}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>,
                                document.body
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    
                    <div className="workflow-arrow down-arrow arrow-from-process">
                      <div className="arrow-line"></div>
                    </div>
                    
                    {/* 结束节点 - 输出结果 */}
                    <div className="workflow-node end-node">
                      <div className="node-icon result-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5 12 2"></polygon>
                          <line x1="12" y1="22" x2="12" y2="15.5"></line>
                          <polyline points="22 8.5 12 15.5 2 8.5"></polyline>
                        </svg>
                      </div>
                      <div className="node-text">{t('apps.detail.agentDetails.outputResult', '输出结果')}</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="crew-stats-inline">
                  <div className="stat-group">
                    <div className="stat-row">
                                              <h3 className="stat-title">{t('apps.detail.agentDetails.agent', '智能体')}</h3>
                      <div className="stat-icons">
                        <div className="no-data">{t('apps.detail.agentDetails.noData', '暂无智能体数据')}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {selectedTab && selectedTab !== 'crewAi' && selectedTab !== 'web_html' && (
            <div className="markdown-section">
              <div className="markdown-body">
                <ReactMarkdown>
                  {getDocContent(selectedTab)}
                </ReactMarkdown>
              </div>
            </div>
          )}
          {selectedTab === 'web_html' && (
            <div className="web-html-section">
              {/* 切换按钮 */}
              <div className="web-html-tabs">
                <button
                  className={`web-html-tab ${webHtmlViewMode === 'code' ? 'active' : ''}`}
                  onClick={() => handleToggleViewMode('code')}
                >
                  {t('apps.detail.tasks.webHtmlCode', '代码编辑')}
                </button>
                <button
                  className={`web-html-tab ${webHtmlViewMode === 'preview' ? 'active' : ''}`}
                  onClick={() => handleToggleViewMode('preview')}
                  disabled={isGeneratingPreview}
                >
                  {isGeneratingPreview ? t('apps.loading', '生成中...') : t('apps.detail.tasks.webHtmlPreview', '预览页面')}
                </button>
              </div>

              {/* 内容区域 */}
              <div className="web-html-content">
                {webHtmlViewMode === 'code' ? (
                  <div className="web-html-code-editor">
                    <h3>{t('apps.detail.tasks.webHtmlCode', '前端页面代码')}</h3>
                    <textarea
                      value={webHtmlCode}
                      onChange={(e) => setWebHtmlCode(e.target.value)}
                      placeholder={t('apps.detail.tasks.webHtmlCodePlaceholder', '在此编辑前端页面代码...')}
                      className="web-html-textarea"
                    />
                  </div>
                ) : (
                  <div className="web-html-preview-container">
                    <div className="web-html-preview-header">
                      <h3>{t('apps.detail.tasks.webHtmlPreview', '前端页面预览')}</h3>
                      <button
                        className="preview-fullscreen-button"
                        onClick={handleOpenPreviewModal}
                        disabled={!isPreviewMode}
                        title={t('apps.detail.tasks.openFullscreenPreview', '全屏预览')}
                      >
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                          <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                        </svg>
                        {t('apps.detail.tasks.fullscreen', '全屏')}
                      </button>
                    </div>
                    {isPreviewMode ? (
                      <iframe
                        src={previewUrl}
                        title={t('apps.detail.tasks.webHtmlPreview', '前端页面预览')}
                        className="web-html-iframe"
                      />
                    ) : (
                      <div className="web-html-preview-placeholder">
                        {t('apps.detail.tasks.webHtmlPreviewPlaceholder', '点击"预览页面"按钮生成预览')}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 操作按钮 */}
              {webHtmlViewMode === 'code' && (
                <div className="web-html-actions">
                  <button
                    className="generate-preview-button"
                    onClick={handleGeneratePreview}
                    disabled={isGeneratingPreview}
                  >
                    {isGeneratingPreview ? t('apps.loading', '生成中...') : t('apps.detail.tasks.generatePreview', '生成预览')}
                  </button>
                </div>
              )}

              {webHtmlViewMode === 'preview' && isPreviewMode && (
                <div className="web-html-actions">
                  <button
                    className="back-to-code-button"
                    onClick={() => setWebHtmlViewMode('code')}
                  >
                    {t('apps.detail.tasks.backToCode', '返回代码编辑')}
                  </button>
                  <button
                    className="regenerate-preview-button"
                    onClick={handleGeneratePreview}
                    disabled={isGeneratingPreview}
                  >
                    {isGeneratingPreview ? t('apps.loading', '重新生成中...') : t('apps.detail.tasks.regeneratePreview', '重新生成预览')}
                  </button>
                </div>
              )}

              {previewError && (
                <div className="preview-error">
                  {t('apps.detail.tasks.previewError', '预览错误:')} {previewError}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {selectedDoc && (
        <div className="doc-modal">
          <div className="doc-content">
            <div className="doc-header">
              <h2>{selectedDoc.title}</h2>
              <div className="header-actions">
                <button 
                  className="close-button" 
                  onClick={() => setSelectedDoc(null)}
                  title={t('apps.detail.agentDetails.close', '关闭')}
                >
                  ×
                </button>
              </div>
            </div>
            <div className="doc-body">
              <textarea
                className="doc-editor"
                value={editedText}
                onChange={(e) => setEditedText(e.target.value)}
                placeholder="在此编辑文档内容..."
              />
            </div>
            <div className="doc-footer">
              <div className="footer-actions">
                <button 
                  className="cancel-button" 
                  onClick={() => setSelectedDoc(null)}
                  disabled={isSaving}
                >
                  {t('createApp.editModal.cancel', '取消')}
                </button>
                <button 
                  className="save-button" 
                  onClick={handleSaveDoc}
                  disabled={isSaving}
                >
                  {isSaving ? t('apps.loading', '保存中...') : t('createApp.editModal.save', '保存')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedDetail && (
        <div className="doc-modal">
          <div className="doc-content">
            <div className="doc-header">
              <h2>{t('apps.detail.tasks.agentDetails')}</h2>
              <div className="header-actions">
                <button 
                  className="close-button" 
                  onClick={() => {
                    setSelectedDetail(null);
                    setEditedDetail(null);
                  }}
                  title={t('apps.detail.agentDetails.close', '关闭')}
                >
                  ×
                </button>
              </div>
            </div>
            <div className="doc-body detail-body">
              <div className="detail-section">
                <h3>{formatName((editedDetail?.name || selectedDetail?.name) ?? '')}</h3>
                <div className="detail-item">
                  <label>{t('apps.detail.agentDetails.goal', '目标')}：</label>
                  {editedDetail?.isEditing ? (
                    <textarea
                      value={editedDetail.goal}
                      onChange={(e) => editedDetail && setEditedDetail({...editedDetail, goal: e.target.value})}
                      className="edit-textarea"
                      title={t('apps.detail.agentDetails.goal', '目标')}
                      placeholder={t('apps.detail.agentDetails.goalPlaceholder', '请输入智能体目标...')}
                    />
                  ) : (
                    <div>{selectedDetail?.goal ?? ''}</div>
                  )}
                </div>
                <div className="detail-item">
                  <label>{t('apps.detail.agentDetails.background', '背景')}：</label>
                  {editedDetail?.isEditing ? (
                    <textarea
                      value={editedDetail.backstory}
                      onChange={(e) => editedDetail && setEditedDetail({...editedDetail, backstory: e.target.value})}
                      className="edit-textarea"
                      title={t('apps.detail.agentDetails.background', '背景')}
                      placeholder={t('apps.detail.agentDetails.backgroundPlaceholder', '请输入智能体背景...')}
                    />
                  ) : (
                    <div>{selectedDetail?.backstory ?? ''}</div>
                  )}
                </div>
                <div className="detail-item">
                  <label>{t('apps.detail.agentDetails.taskDescription', '任务描述')}：</label>
                  {editedDetail?.isEditing ? (
                    <textarea
                      value={editedDetail.task_description}
                      onChange={(e) => setEditedDetail({...editedDetail, task_description: e.target.value})}
                      className="edit-textarea"
                      title={t('apps.detail.agentDetails.taskDescription', '任务描述')}
                      placeholder={t('apps.detail.agentDetails.taskDescriptionPlaceholder', '请输入任务描述...')}
                    />
                  ) : (
                    <div className="task-description">{selectedDetail?.task_description ?? ''}</div>
                  )}
                </div>
                <div className="detail-item">
                  <label>{t('apps.detail.agentDetails.expectedOutput', '预期输出')}：</label>
                  {editedDetail?.isEditing ? (
                    <textarea
                      value={editedDetail.task_expected_output}
                      onChange={(e) => setEditedDetail({...editedDetail, task_expected_output: e.target.value})}
                      className="edit-textarea"
                      title={t('apps.detail.agentDetails.expectedOutput', '预期输出')}
                      placeholder={t('apps.detail.agentDetails.expectedOutputPlaceholder', '请输入预期输出...')}
                    />
                  ) : (
                    <div className="task-expected-output">{selectedDetail?.task_expected_output ?? ''}</div>
                  )}
                </div>
                {/* Crew Tools 展示/编辑 */}
                {/* <div className="detail-item">
                  <label>工具：</label>
                  {editedDetail?.isEditing ? (
                      <Select
                        mode="multiple"
                        style={{ width: '100%' }}
                        placeholder={'请选择Crew工具'}
                        value={editedDetail.crewTools || []}
                        onChange={vals => setEditedDetail({ ...editedDetail, crewTools: vals })}
                        optionLabelProp="label"
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                          String(option?.label ?? '').toLowerCase().includes(input.toLowerCase()) ||
                          String(option?.description ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                      >
                        {crewToolsList.map(tool => (
                          <Select.Option key={tool.tool_name} value={tool.tool_name} label={tool.tool_name} description={tool.description}>
                            <div style={{display:'flex',flexDirection:'column'}}>
                              <span style={{fontWeight:600}}>{tool.tool_name}</span>
                              <span style={{fontSize:12,color:'#888'}}>{tool.description}</span>
                            </div>
                          </Select.Option>
                        ))}
                      </Select>
                  ) : (
                    <div>
                      {(selectedDetail.crewTools && selectedDetail.crewTools.length > 0)
                        ? selectedDetail.crewTools.map(name => {
                            const tool = crewToolsList.find(t => t.tool_name === name);
                            return (
                              <div key={name} style={{marginBottom: 4}}>
                                <b>{name}</b> {tool?.description || ''}
                              </div>
                            );
                          })
                        : '无'}
                    </div>
                  )}
                </div> */}
                {/* MCP Tools 展示/编辑 */}
                <div className="detail-item">
                  <label>MCP Server：</label>
                  {editedDetail?.isEditing ? (
                    <div style={{maxWidth: 500}}>
                      <Select
                        mode="multiple"
                        style={{ width: '100%' }}
                        placeholder={loadingMcpTools ? '加载中...' : '请选择MCP Server'}
                        value={editedDetail.mcpTools}
                        loading={loadingMcpTools}
                        onChange={vals => setEditedDetail({ ...editedDetail, mcpTools: vals })}
                        optionLabelProp="label"
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                          String(option?.label ?? '').toLowerCase().includes(input.toLowerCase()) ||
                          String(option?.description ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                      >
                        {mcpToolsList.map(server => (
                          <Select.Option key={server.mcpId} value={server.mcpId} label={server.name} description={server.description}>
                            <div style={{display:'flex',flexDirection:'column'}}>
                              <span style={{fontWeight:600}}>{server.name}</span>
                              <span style={{fontSize:12,color:'#888'}}>{server.description}</span>
                            </div>
                          </Select.Option>
                        ))}
                      </Select>
                      {loadingMcpTools && <div style={{color:'#888',marginTop:8}}>MCP Server列表加载中...</div>}
                      {!loadingMcpTools && mcpToolsList.length === 0 && <div style={{color:'red',marginTop:8}}>MCP Server列表获取失败，可能是跨域问题，请联系管理员配置后端代理。</div>}
                    </div>
                  ) : (
                    <div>
                      {(selectedDetail.mcpTools && selectedDetail.mcpTools.length > 0)
                        ? selectedDetail.mcpTools.map(mcpId => {
                            const server = mcpToolsList.find(s => s.mcpId === mcpId);
                            return (
                              <div key={mcpId} style={{marginBottom: 4}}>
                                <b>{server ? server.name : mcpId}</b> {server ? server.description : ''}
                              </div>
                            );
                          })
                        : '无'}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="doc-footer" style={{ gap: '20px' }}>
              {editedDetail?.isEditing ? (
                <>
                  <button 
                    className="cancel-button" 
                    onClick={handleCancel}
                    disabled={isSaving}
                    style={{ marginRight: '10px' }}
                  >
                    {t('createApp.editModal.cancel', '取消')}
                  </button>
                  <button 
                    className="save-button" 
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? t('apps.loading', '保存中...') : t('createApp.editModal.save', '保存')}
                  </button>
                </>
              ) : (
                <>
                  <button 
                    className="edit-button" 
                    onClick={handleEdit}
                    style={{ marginRight: '20px' }}
                  >
                    {t('apps.detail.agentDetails.edit', '编辑')}
                  </button>
                  <button 
                    className="cancel-button" 
                    onClick={() => setSelectedDetail(null)}
                  >
                    {t('apps.detail.agentDetails.close', '关闭')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {toolsModal.open && (
        <div className="doc-modal">
          <div className="doc-content">
            <div className="doc-header">
              <h2>编辑Tools</h2>
              <div className="header-actions">
                <button className="close-button" onClick={handleCloseToolsModal} title="关闭">×</button>
              </div>
            </div>
            <div className="doc-body detail-body">
              <div className="detail-section">
                <div className="detail-item">
                  <label>Crew Tools（逗号分隔）:</label>
                  <textarea
                    value={toolsModal.crewTools.join(', ')}
                    onChange={e => handleToolsChange('crew', e.target.value)}
                    className="edit-textarea"
                    placeholder="如：CSVSearchTool, WebSearchTool"
                    style={{ minHeight: 40 }}
                  />
                </div>
                <div className="detail-item">
                  <label>MCP Tools（逗号分隔）:</label>
                  <textarea
                    value={toolsModal.mcpTools.join(', ')}
                    onChange={e => handleToolsChange('mcp', e.target.value)}
                    className="edit-textarea"
                    placeholder="如：工具ID1, 工具ID2"
                    style={{ minHeight: 40 }}
                  />
                </div>
              </div>
            </div>
            <div className="doc-footer" style={{ gap: '20px' }}>
              <button className="cancel-button" onClick={handleCloseToolsModal} disabled={toolsModal.saving}>取消</button>
              <button className="save-button" onClick={handleSaveTools} disabled={toolsModal.saving}>
                {toolsModal.saving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 预览模态框 */}
      {isPreviewModalOpen && (
        <div className="preview-modal">
          <div className="preview-modal-content">
            <div className="preview-modal-header">
              <h3>{t('apps.detail.tasks.webHtmlPreview', '前端页面预览')}</h3>
              <button
                className="preview-modal-close"
                onClick={handleClosePreviewModal}
                title={t('apps.detail.agentDetails.close', '关闭')}
              >
                ×
              </button>
            </div>
            <div className="preview-modal-body">
              {isPreviewMode && previewUrl ? (
                <iframe
                  src={previewUrl}
                  title={t('apps.detail.tasks.webHtmlPreview', '前端页面预览')}
                  className="preview-modal-iframe"
                />
              ) : (
                <div className="preview-modal-placeholder">
                  {t('apps.detail.tasks.previewNotAvailable', '预览不可用')}
                </div>
              )}
            </div>
            <div className="preview-modal-footer">
              <button
                className="regenerate-preview-button"
                onClick={handleGeneratePreview}
                disabled={isGeneratingPreview}
              >
                {isGeneratingPreview ? t('apps.loading', '重新生成中...') : t('apps.detail.tasks.regeneratePreview', '重新生成预览')}
              </button>
              <button
                className="close-modal-button"
                onClick={handleClosePreviewModal}
              >
                {t('apps.detail.agentDetails.close', '关闭')}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .crew-stats {
          margin-top: 20px;
          padding: 20px;
          background: #f8f9fa;
          border-radius: 8px;
          width: 100%;
          box-sizing: border-box;
        }

        .stat-group {
          display: flex;
          flex-direction: column;
          gap: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .stat-row {
          display: flex;
          align-items: center;
          padding: 20px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }

        .stat-title {
          margin: 0;
          min-width: 120px;
          font-size: 16px;
          color: #333;
          font-weight: 500;
        }

        .stat-icons {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          flex: 1;
        }

        .icon-wrapper {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: #f8f9fa;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          min-width: 200px;
          flex: 1;
          max-width: calc(33.33% - 8px);
        }

        .icon-wrapper:hover {
          background: #e9ecef;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .stat-icon {
          width: 24px;
          height: 24px;
          object-fit: contain;
        }

        .icon-text {
          font-size: 14px;
          color: #495057;
          flex: 1;
          text-transform: capitalize;
        }

        .work-icon {
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #1a73e8;
        }

        .work-icon svg {
          width: 100%;
          height: 100%;
        }

        .doc-modal,
        .detail-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .doc-content,
        .detail-content {
          background: white;
          padding: 20px;
          border-radius: 8px;
          max-width: 800px;
          max-height: 80vh;
          overflow-y: auto;
          width: 90%;
        }

        .detail-content h2 {
          margin-top: 0;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 1px solid #eee;
        }

        .detail-content h3 {
          margin-top: 16px;
          margin-bottom: 8px;
          color: #333;
        }

        .detail-content p {
          margin: 8px 0;
          line-height: 1.6;
          color: #666;
        }

        .detail-content button {
          margin-top: 20px;
          padding: 8px 16px;
          background: #1a73e8;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }

        .detail-content button:hover {
          background: #1557b0;
        }

        .work-detail {
          padding: 0 20px;
        }

        .work-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 24px;
        }

        .work-name {
          font-size: 24px;
          color: #1a73e8;
          margin: 0;
          font-weight: 600;
        }

        .work-tag {
          background: #e8f0fe;
          color: #1a73e8;
          padding: 4px 12px;
          border-radius: 16px;
          font-size: 14px;
          font-weight: 500;
        }

        .work-section {
          margin-bottom: 24px;
        }

        .work-section h4 {
          font-size: 16px;
          color: #202124;
          margin: 0 0 12px 0;
          font-weight: 500;
        }

        .work-description {
          font-size: 15px;
          line-height: 1.6;
          color: #5f6368;
          background: #f8f9fa;
          padding: 16px;
          border-radius: 8px;
          margin: 0;
        }

        .work-detailed {
          background: #ffffff;
          border: 1px solid #e8eaed;
          border-radius: 8px;
          padding: 20px;
        }

        .edit-textarea {
          width: 100%;
          padding: 16px;
          font-size: 14px;
          line-height: 1.6;
          border: 1px solid #e8eaed;
          border-radius: 8px;
          resize: vertical;
          font-family: monospace;
          background: #f8f9fa;
          /* 高度自适应，最多12行，内容少时自动增高，内容多时出现滚动条 */
          min-height: calc(1.6em * 3 + 32px); /* 3行+padding */
          max-height: calc(1.6em * 12 + 32px); /* 12行+padding */
          overflow-y: auto;
          transition: border-color 0.2s ease;
        }

        .edit-textarea:focus {
          outline: none;
          border-color: #1a73e8;
          box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.1);
        }

        .markdown-content {
          font-size: 15px;
          line-height: 1.7;
          color: #202124;
        }

        .markdown-content h1,
        .markdown-content h2,
        .markdown-content h3,
        .markdown-content h4,
        .markdown-content h5,
        .markdown-content h6 {
          color: #1a73e8;
          margin-top: 24px;
          margin-bottom: 16px;
        }

        .markdown-content p {
          margin: 16px 0;
        }

        .markdown-content code {
          padding: 0.2em 0.4em;
          margin: 0;
          font-size: 85%;
          background-color: rgba(27, 31, 35, 0.05);
          border-radius: 3px;
          font-family: SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace;
        }

        .markdown-content pre {
          padding: 16px;
          overflow: auto;
          font-size: 85%;
          line-height: 1.45;
          background-color: #f6f8fa;
          border-radius: 3px;
          margin-top: 0;
          margin-bottom: 16px;
        }

        .markdown-content pre code {
          padding: 0;
          margin: 0;
          font-size: 100%;
          word-break: normal;
          white-space: pre;
          background: transparent;
          border: 0;
        }

        .markdown-content blockquote {
          margin: 0 0 16px;
          padding: 0 1em;
          color: #6a737d;
          border-left: 0.25em solid #dfe2e5;
        }

        .markdown-content ul,
        .markdown-content ol {
          padding-left: 2em;
          margin-top: 0;
          margin-bottom: 16px;
        }

        .markdown-content table {
          display: block;
          width: 100%;
          overflow: auto;
          margin-top: 0;
          margin-bottom: 16px;
          border-spacing: 0;
          border-collapse: collapse;
        }

        .markdown-content th {
          font-weight: 600;
          padding: 6px 13px;
          border: 1px solid #dfe2e5;
          background-color: #f6f8fa;
        }

        .markdown-content td {
          padding: 6px 13px;
          border: 1px solid #dfe2e5;
        }

        .markdown-content tr {
          background-color: #fff;
          border-top: 1px solid #c6cbd1;
        }

        .markdown-content tr:nth-child(2n) {
          background-color: #f6f8fa;
        }

        .markdown-content img {
          max-width: 100%;
          box-sizing: border-box;
          border-radius: 4px;
        }

        .markdown-content hr {
          height: 0.25em;
          padding: 0;
          margin: 24px 0;
          background-color: #e1e4e8;
          border: 0;
        }

        .markdown-content a {
          color: #0366d6;
          text-decoration: none;
        }

        .markdown-content a:hover {
          text-decoration: underline;
        }

        .markdown-content strong {
          font-weight: 600;
        }

        .markdown-content em {
          font-style: italic;
        }

        .markdown-content del {
          text-decoration: line-through;
        }

        .markdown-content details {
          margin: 16px 0;
        }

        .markdown-content details summary {
          cursor: pointer;
        }

        .markdown-content kbd {
          display: inline-block;
          padding: 3px 5px;
          font: 11px SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace;
          line-height: 10px;
          color: #444d56;
          vertical-align: middle;
          background-color: #fafbfc;
          border: 1px solid #d1d5da;
          border-radius: 3px;
          box-shadow: inset 0 -1px 0 #d1d5da;
        }

        .progress-timeline {
          display: flex;
          flex-direction: column;
          padding: 1rem;
          position: relative;
          width: 100%;
        }

        .timeline-item {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          position: relative;
        }

        .timeline-line {
          width: 2px;
          height: 40px;
          background: #4CAF50;
          margin: 4px 0 4px 15px;
          position: relative;
        }

        .progress-tab {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.75rem 1rem;
          cursor: pointer;
          transition: all 0.2s ease;
          border-radius: 8px;
          position: relative;
          background: transparent;
          width: 100%;
        }

        .progress-tab:hover {
          background: rgba(76, 175, 80, 0.1);
        }

        .progress-tab.active {
          background: rgba(76, 175, 80, 0.15);
        }

        .tab-icon-wrapper {
          width: 32px;
          height: 32px;
          border: 2px solid #4CAF50;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: white;
          position: relative;
          transition: all 0.3s ease;
          flex-shrink: 0;
        }

        .tab-icon-wrapper.completed {
          background: #4CAF50;
        }

        .check-icon {
          width: 20px;
          height: 20px;
          fill: white;
        }

        .empty-circle {
          width: 12px;
          height: 12px;
          background: #4CAF50;
          border-radius: 50%;
        }

        .tab-name {
          font-size: 1rem;
          color: #333;
          font-weight: 500;
          flex: 1;
        }

        .progress-tab.active .tab-name {
          color: #4CAF50;
        }

        .progress-tabs {
          width: 280px;
          background: #f8f9fa;
          border-radius: 12px 0 0 12px;
          border-right: 1px solid #e5e7eb;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .doc-editor {
          width: 100%;
          height: 500px;
          padding: 16px;
          font-size: 14px;
          line-height: 1.6;
          border: 1px solid #e8eaed;
          border-radius: 8px;
          resize: vertical;
          font-family: monospace;
          background: #f8f9fa;
        }

        .doc-editor:focus {
          outline: none;
          border-color: #1a73e8;
          box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.1);
        }

        .doc-footer {
          padding: 16px 24px;
          border-top: 1px solid #e8eaed;
          display: flex;
          justify-content: flex-end;
          gap: 20px;
        }

        .save-button {
          padding: 8px 24px;
          background: #1a73e8;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .save-button:hover {
          background: #1557b0;
        }

        .save-button:disabled {
          background: #ccc;
          cursor: not-allowed;
        }

        .header-actions {
          display: flex;
          gap: 12px;
        }

        .close-button {
          background: none;
          border: none;
          font-size: 24px;
          color: #666;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 4px;
          transition: all 0.2s;
          line-height: 1;
        }

        .close-button:hover {
          background: #f1f3f4;
          color: #333;
        }

        .footer-actions {
          display: flex;
          gap: 12px;
        }

        .cancel-button {
          padding: 8px 24px;
          background: #f1f3f4;
          color: #3c4043;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .cancel-button:hover {
          background: #e8eaed;
        }

        .cancel-button:disabled {
          background: #f1f3f4;
          color: #999;
          cursor: not-allowed;
        }

        .icon-wrapper.clickable {
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .icon-wrapper.clickable:hover {
          background: #e8f0fe;
          transform: translateY(-2px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .detail-body {
          padding: 24px;
        }

        .detail-section {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 8px;
        }

        .detail-section h3 {
          margin: 0 0 20px 0;
          color: #1a73e8;
          font-size: 20px;
          font-weight: 500;
        }

        .detail-item {
          margin-bottom: 16px;
        }

        .detail-item:last-child {
          margin-bottom: 0;
        }

        .detail-item label {
          display: block;
          font-weight: 500;
          color: #333;
          margin-bottom: 8px;
        }

        .detail-item > div {
          background: white;
          padding: 16px;
          border-radius: 4px;
          border: 1px solid #e8eaed;
          line-height: 1.6;
          white-space: pre-wrap;
          font-size: 14px;
          color: #333;
        }

        .task-description, .task-expected-output {
          max-height: 300px;
          overflow-y: auto;
          font-family: monospace;
          white-space: pre-wrap;
          font-size: 13px !important;
          background: #f8f9fa !important;
        }

        .workflow-diagram {
          margin: 20px 0;
          padding: 20px;
          background: #f8f9fa;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          overflow: hidden;
        }

        .workflow-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding: 0 10px;
        }

        .workflow-title {
          margin: 0;
          color: #1a73e8;
          font-size: 18px;
          font-weight: 500;
          flex: 1;
          text-align: center;
        }

        .schedule-toggle-container {
          display: flex;
          align-items: center;
        }

        .schedule-toggle-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: #f8f9fa;
          border: 1px solid #e8eaed;
          border-radius: 20px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 14px;
          color: #5f6368;
          font-weight: 500;
          min-width: 120px;
          justify-content: center;
        }

        .schedule-toggle-button:hover:not(:disabled) {
          background: #e8f0fe;
          border-color: #1a73e8;
          color: #1a73e8;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(26, 115, 232, 0.15);
        }

        .schedule-toggle-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .schedule-type-text {
          font-size: 13px;
          font-weight: 500;
        }

        .toggle-icon {
          width: 16px;
          height: 16px;
          transition: transform 0.3s ease;
        }

        .toggle-icon.rotating {
          animation: rotate 1s linear infinite;
        }

        @keyframes rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .workflow-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 20px; /* 调整间距 */
          padding: 30px 20px;
          background: white;
          border-radius: 8px;
          max-width: 800px;
          margin: 0 auto;
          position: relative;
        }
        
        .arrow-to-process {
          margin-bottom: -45px; /* 向下延伸，连接到智能体处理区域 */
          margin-top: -25px; /* 添加负的top margin，让连线直接连接到上方用户节点 */
        }
        
        .arrow-from-process {
          margin-top: -45px; /* 向下移动，穿过处理区域边界 */
          margin-bottom: -25px; /* 增加负的bottom margin，让连线直接连接到下方节点 */
        }

        .agents-process-area {
          width: 480px;
          background-color: #f1f8ff;
          border: 2px solid #1a73e8;
          border-radius: 12px;
          padding: 30px 20px 50px; /* 增加底部内边距，为箭头留出空间 */
          position: relative;
          box-shadow: 0 4px 12px rgba(26, 115, 232, 0.1);
          display: flex;
          flex-direction: column;
          align-items: center;
          margin: 20px 0;
          z-index: 1;
        }

        /* hierarchical布局的agents-process-area动态高度 */
        .workflow-container.hierarchical .agents-process-area {
          height: auto;
          min-height: 580px;
        }

        .workflow-container.hierarchical.agents-5 .agents-process-area {
          min-height: 780px;
        }

        .workflow-container.hierarchical.agents-6 .agents-process-area {
          min-height: 880px;
        }

        .workflow-container.hierarchical.agents-7 .agents-process-area {
          min-height: 980px;
        }

        .workflow-container.hierarchical.agents-8 .agents-process-area {
          min-height: 1080px;
        }

        .process-area-title {
          position: absolute;
          top: -12px;
          background-color: #1a73e8;
          color: white;
          padding: 4px 16px;
          border-radius: 20px;
          font-weight: 500;
          font-size: 14px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .agents-circle-container {
          width: 500px;
          height: 500px;
          position: relative;
          margin: 20px auto;
          display: flex;
          justify-content: center;
          align-items: center;
          background-color: transparent;
          border-radius: 50%;
        }

        /* hierarchical布局的动态高度样式 */
        .workflow-container.hierarchical .agents-circle-container {
          border-radius: 12px;
          height: auto;
          min-height: 500px;
        }

        .workflow-container.hierarchical.agents-5 .agents-circle-container {
          height: 700px;
        }

        .workflow-container.hierarchical.agents-6 .agents-circle-container {
          height: 800px;
        }

        .workflow-container.hierarchical.agents-7 .agents-circle-container {
          height: 900px;
        }

        .workflow-container.hierarchical.agents-8 .agents-circle-container {
          height: 1000px;
        }

        .agent-connections {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 4;
          pointer-events: none;
        }

        .circle-node {
          width: 120px;
          height: 120px;
          z-index: 3;
          background: white;
          margin: 0;
          border: 2px solid #1a73e8;
          transition: box-shadow 0.3s ease, background-color 0.3s ease, border-color 0.3s ease;
          transform-origin: center center;
          position: absolute !important;
        }

        .circle-node:hover {
          box-shadow: 0 0 15px rgba(26, 115, 232, 0.3);
          z-index: 5;
          border-color: #1557b0;
          background: #e8f0fe;
        }

        .workflow-node {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 15px;
          border-radius: 8px;
          min-width: 150px;
          text-align: center;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          transition: all 0.2s ease;
          gap: 10px;
        }

        .down-arrow {
          height: 80px; /* 增加高度 */
          width: 4px; /* 增加宽度 */
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          z-index: 10;
        }

        .down-arrow .arrow-line {
          height: 70px; /* 增加高度 */
          background: #1a73e8;
          animation: pulse 2s infinite;
          position: relative;
          z-index: 10;
          width: 4px; /* 增加宽度 */
        }

        @keyframes pulse {
          0% {
            opacity: 0.5;
          }
          50% {
            opacity: 1;
          }
          100% {
            opacity: 0.5;
          }
        }

        .down-arrow .arrow-line:after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 10px solid transparent; /* 增加箭头大小 */
          border-right: 10px solid transparent; /* 增加箭头大小 */
          border-top: 12px solid #1a73e8; /* 增加箭头大小 */
          z-index: 10;
        }

        .down-arrow .arrow-text {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: #1a73e8; /* 改变背景色 */
          border: none;
          color: white; /* 改变文字颜色 */
          font-weight: 500;
          z-index: 11;
          white-space: nowrap;
          padding: 6px 12px; /* 增加内边距 */
          border-radius: 20px; /* 圆角 */
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2); /* 添加阴影 */
        }

        .start-node, .end-node {
          background: #e6f4ea;
          border: 2px solid #34a853;
          min-width: 180px;
          height: auto;
          padding: 20px;
          z-index: 3;
        }

        .start-node:hover, .end-node:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 16px rgba(52, 168, 83, 0.2);
        }

        .agent-node {
          background: #e8f0fe;
          border: 2px solid #1a73e8;
          cursor: pointer;
        }

        .agent-node:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }

        .start-node {
          background: #e6f4ea;
          border: 2px solid #34a853;
        }

        .end-node {
          background: #e6f4ea;
          border: 2px solid #34a853;
        }

        .node-icon {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .user-icon {
          color: #34a853;
        }

        .agent-icon {
          color: #1a73e8;
        }

        .result-icon {
          color: #34a853;
        }

        .node-text {
          font-size: 14px;
          font-weight: 500;
          color: #333;
        }

        .workflow-arrow {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
          gap: 4px;
        }

        .arrow-line {
          width: 2px;
          height: 30px;
          background: #bdc1c6;
          position: relative;
        }

        .arrow-line:after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 8px solid #bdc1c6;
        }

        .arrow-text {
          font-size: 12px;
          color: #5f6368;
          background: #f2f2f2;
          padding: 2px 8px;
          border-radius: 12px;
        }

        .custom-spinner {
          display: inline-block;
          width: 20px;
          height: 20px;
          border: 3px solid rgba(24, 144, 255, 0.2);
          border-radius: 50%;
          border-left-color: #1890ff;
          animation: customSpin 1s linear infinite;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 2;
        }

        @keyframes customSpin {
          0% {
            transform: translate(-50%, -50%) rotate(0deg);
          }
          100% {
            transform: translate(-50%, -50%) rotate(360deg);
          }
        }

        .no-data {
          padding: 20px;
          text-align: center;
          color: #666;
          font-size: 14px;
          background: #f8f9fa;
          border-radius: 8px;
          width: 100%;
        }

        .edit-button {
          padding: 8px 24px;
          background: #1a73e8;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          margin-right: 10px;
        }

        .edit-button:hover {
          background: #1557b0;
        }

        .crew-ai-content {
          max-width: 1400px !important;
          width: 100% !important;
          margin: 0 auto;
          padding: 20px 40px !important;
        }

        .crew-ai-content .workflow-diagram {
          max-width: none !important;
          width: 100% !important;
        }

        .crew-ai-content .workflow-container {
          max-width: 1200px !important;
          width: 100% !important;
        }

        .crew-ai-content .agents-process-area {
          width: 600px !important;
        }

        .crew-ai-content .agents-circle-container {
          width: 500px !important;
          height: 500px !important;
        }

        /* crew-ai-content中hierarchical布局的动态高度覆盖 */
        .crew-ai-content .workflow-container.hierarchical .agents-circle-container {
          height: auto !important;
          min-height: 500px !important;
        }

        .crew-ai-content .workflow-container.hierarchical.agents-5 .agents-circle-container {
          height: 700px !important;
        }

        .crew-ai-content .workflow-container.hierarchical.agents-6 .agents-circle-container {
          height: 800px !important;
        }

        .crew-ai-content .workflow-container.hierarchical.agents-7 .agents-circle-container {
          height: 900px !important;
        }

        .crew-ai-content .workflow-container.hierarchical.agents-8 .agents-circle-container {
          height: 1000px !important;
        }

        /* 前端页面相关样式 */
        .web-html-section {
          padding: 20px;
          max-width: 100%;
        }

        .web-html-tabs {
          display: flex;
          border-bottom: 2px solid #e5e7eb;
          margin-bottom: 20px;
          gap: 0;
        }

        .web-html-tab {
          padding: 12px 24px;
          border: none;
          background: #f8f9fa;
          color: #5f6368;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 14px;
          font-weight: 500;
          border-bottom: 3px solid transparent;
          position: relative;
        }

        .web-html-tab:first-child {
          border-radius: 8px 0 0 0;
        }

        .web-html-tab:last-child {
          border-radius: 0 8px 0 0;
        }

        .web-html-tab:hover:not(:disabled) {
          background: #e8f0fe;
          color: #1a73e8;
        }

        .web-html-tab.active {
          background: white;
          color: #1a73e8;
          border-bottom-color: #1a73e8;
          font-weight: 600;
        }

        .web-html-tab:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .web-html-content {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0 0 8px 8px;
          min-height: 600px;
          display: flex;
          flex-direction: column;
        }

        .web-html-code-editor {
          padding: 20px;
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .web-html-code-editor h3 {
          margin: 0 0 15px 0;
          font-size: 16px;
          font-weight: 600;
          color: #333;
        }

        .web-html-textarea {
          flex: 1;
          min-height: 500px;
          resize: vertical;
          border: 1px solid #ddd;
          border-radius: 4px;
          padding: 16px;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 13px;
          line-height: 1.5;
          background: #f8f9fa;
          transition: border-color 0.2s;
        }

        .web-html-textarea:focus {
          outline: none;
          border-color: #1a73e8;
          box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.1);
        }

        .web-html-preview-container {
          padding: 20px;
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .web-html-preview-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }

        .web-html-preview-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #333;
        }

        .preview-fullscreen-button {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          background: #1a73e8;
          color: white;
          border: none;
          border-radius: 4px;
          font-size: 13px;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .preview-fullscreen-button:hover:not(:disabled) {
          background: #1557b0;
        }

        .preview-fullscreen-button:disabled {
          background: #d9d9d9;
          cursor: not-allowed;
        }

        .web-html-iframe {
          width: 100%;
          flex: 1;
          min-height: 450px;
          border: 1px solid #ddd;
          border-radius: 4px;
          background: white;
        }

        .web-html-preview-placeholder {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px dashed #ddd;
          border-radius: 4px;
          color: #666;
          text-align: center;
          background: #f8f9fa;
          min-height: 450px;
        }

        .web-html-actions {
          margin-top: 20px;
          display: flex;
          gap: 12px;
          justify-content: center;
        }

        .generate-preview-button,
        .back-to-code-button,
        .regenerate-preview-button {
          padding: 10px 20px;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .generate-preview-button,
        .regenerate-preview-button {
          background: #1a73e8;
          color: white;
        }

        .generate-preview-button:hover:not(:disabled),
        .regenerate-preview-button:hover:not(:disabled) {
          background: #1557b0;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(26, 115, 232, 0.3);
        }

        .back-to-code-button {
          background: #f8f9fa;
          color: #5f6368;
          border: 1px solid #e5e7eb;
        }

        .back-to-code-button:hover {
          background: #e8f0fe;
          color: #1a73e8;
          border-color: #1a73e8;
        }

        .generate-preview-button:disabled,
        .regenerate-preview-button:disabled {
          background: #d9d9d9;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        /* 预览模态框样式 */
        .preview-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
          padding: 20px;
        }

        .preview-modal-content {
          background: white;
          border-radius: 12px;
          width: 95vw;
          height: 90vh;
          max-width: 1400px;
          max-height: 900px;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
        }

        .preview-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px 16px;
          border-bottom: 1px solid #e5e7eb;
        }

        .preview-modal-header h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #333;
        }

        .preview-modal-close {
          background: none;
          border: none;
          font-size: 28px;
          color: #666;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 4px;
          transition: all 0.2s;
          line-height: 1;
        }

        .preview-modal-close:hover {
          background: #f1f3f4;
          color: #333;
        }

        .preview-modal-body {
          flex: 1;
          padding: 0;
          overflow: hidden;
        }

        .preview-modal-iframe {
          width: 100%;
          height: 100%;
          border: none;
          background: white;
        }

        .preview-modal-placeholder {
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #666;
          font-size: 16px;
        }

        .preview-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 24px 20px;
          border-top: 1px solid #e5e7eb;
          background: #f8f9fa;
        }

        .close-modal-button {
          padding: 10px 20px;
          background: #f8f9fa;
          color: #5f6368;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .close-modal-button:hover {
          background: #e8f0fe;
          color: #1a73e8;
          border-color: #1a73e8;
        }

        .preview-error {
          margin-top: 16px;
          padding: 12px 16px;
          background: #fff2f0;
          border: 1px solid #ffccc7;
          border-radius: 6px;
          color: #ff4d4f;
          font-size: 14px;
          text-align: center;
        }

        @media (max-width: 768px) {
          .preview-modal {
            padding: 10px;
          }

          .preview-modal-content {
            width: 100%;
            height: 95vh;
          }

          .web-html-tabs {
            flex-direction: column;
          }

          .web-html-tab {
            border-radius: 0;
            border-bottom: 1px solid #e5e7eb;
          }

          .web-html-tab:first-child {
            border-radius: 8px 8px 0 0;
          }

          .web-html-tab:last-child {
            border-radius: 0;
            border-bottom: none;
          }

          .web-html-content {
            border-radius: 0 0 8px 8px;
          }

          .web-html-actions {
            flex-direction: column;
          }
        }
      `}</style>
      
      {/* 调试信息窗口 */}
      {debugInfo && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: '#fff',
          border: '2px solid #1890ff',
          borderRadius: '8px',
          padding: '16px',
          maxWidth: '400px',
          zIndex: 9999,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          fontFamily: 'monospace',
          fontSize: '12px',
          lineHeight: '1.4'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '8px',
            borderBottom: '1px solid #eee',
            paddingBottom: '8px'
          }}>
            <strong style={{ color: '#1890ff' }}>调试信息</strong>
            <button 
              onClick={() => setDebugInfo('')}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '16px',
                cursor: 'pointer',
                color: '#999'
              }}
            >×</button>
          </div>
          <div style={{ 
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            maxHeight: '200px',
            overflow: 'auto'
          }}>
            {debugInfo}
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskProgress;